import MatrizDePermisos from '../experiencia/MatrizDePermisos';
import TarjetasDePersona from '../nfc/TarjetasDePersona';
import Expediente from '../archivos/Expediente';
import { useCallback, useEffect, useState } from 'react';
import { Users, Copy, Send, Settings2, IdCard, Printer, Upload, FileDown, Bot, KeyRound, Plus, Trash2 } from 'lucide-react';
import { api, ErrorApi } from '../api';
import { useAuth } from '../lib/contextoAuth';
import { etiquetaRol, JERARQUIA } from '../lib/roles';
import ListaEstandar from '../ui/ListaEstandar';
import ChipEstado from '../ui/ChipEstado';
import EstadoVacio from '../ui/EstadoVacio';
import TiempoRelativo from '../ui/TiempoRelativo';
import NotaExplicativa from '../ui/NotaExplicativa';
import Ayuda from '../ui/Ayuda';
import BotonExportar from '../ui/BotonExportar';
import ModalConfirmar from '../ui/ModalConfirmar';
import Modal from '../ui/Modal';
import CodigoQR from '../ui/CodigoQR';
import HojaDeGafete from '../terminal/HojaDeGafete';
import { Seccion, CampoValidado } from '../ui/Formulario';
import { ESTADO_USUARIO, ESTADO_INVITACION, estadoUsuario } from '../ui/estados';
// CORREO F2 (carril accesos, 14 sep 2026): los caminos de entrega de una invitación.
import EntregaDeInvitacion from '../accesos/EntregaDeInvitacion';
import { mensajeDeInvitacion, enlaceWhatsapp } from '../accesos/compartirPorWhatsapp';
import { Cargando, ErrorConReintento } from '../ui/EstadoDePantalla';
import { motivoDeError } from '../ui/estados';
import Motivo from '../experiencia/Motivo';
import PermisosQueCaducan from '../accesos/PermisosQueCaducan';
import { errorDeFechaFin, textoDeVigencia } from '../accesos/vigenciaDePermiso';
import CampoContrasena from '../ui/CampoContrasena';
import LlavesDeUnaPersona from '../seguridad/LlavesDeUnaPersona';
import { veLlavesDeOtros } from '../seguridad/llavesDeLaCuenta';

const ROLES_INVITABLES = ['ADMIN', 'ENCARGADO', 'OPERADOR', 'CONSULTA'];

// L1-6 · los estados por los que se puede filtrar. Salen del mismo modelo
// de cuatro estados de siempre (activo/suspendido/bloqueado/baja); «nunca»
// no es un estado, es «activo y todavía sin primer acceso» —la gente a la
// que hay que perseguir— y por eso va aparte.
const ESTADOS_FILTRO = [
  { valor: '', etiqueta: 'Cualquier estado' },
  { valor: 'activos', etiqueta: 'Activos' },
  { valor: 'suspendidos', etiqueta: 'Suspendidos' },
  { valor: 'bloqueados', etiqueta: 'Bloqueados' },
  { valor: 'nunca', etiqueta: 'Nunca han entrado' },
  { valor: 'baja', etiqueta: 'Dados de baja' },
];

const ORDENES = [
  { valor: 'ultimo_acceso', etiqueta: 'Último acceso (lo más reciente primero)' },
  { valor: 'nombre', etiqueta: 'Nombre (A→Z)' },
  { valor: 'rol', etiqueta: 'Puesto' },
  { valor: 'sucursal', etiqueta: 'Sucursal' },
];

// QUÉ SUCURSALES ALCANZA CADA PERSONA, DE UN VISTAZO.
//
// El 12 de agosto de 2026 el dueño descubrió que llevaba meses asignando
// gente a sucursales creyendo que quedaba acotada, y no lo estaba en
// ningún sitio. Al arreglarlo pidió esto: «quiero ver de un vistazo quién
// está en TODAS y quién en ASIGNADAS».
//
// Y EL AVISO ES LO IMPORTANTE. Alguien en ASIGNADAS sin ninguna sucursal
// NO VA A VER NADA y no va a saber por qué: va a ver un almacén vacío y
// va a pensar que el sistema está roto. Decirlo aquí, en la pantalla donde
// se arregla, es la diferencia entre un permiso a medio dar y un misterio.
function textoAlcance(u) {
  if (u.alcance_sucursales !== 'ASIGNADAS') return 'Todas las sucursales';
  if (u.sin_ninguna_sucursal) return 'Sin ninguna sucursal';
  return u.sucursal ? `Solo ${u.sucursal}` : 'Solo las asignadas';
}

function ChipAlcance({ u }) {
  if (u.sin_ninguna_sucursal) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5
                   text-xs font-medium text-red-700 ring-1 ring-red-200"
        title="Esta persona no verá ninguna sucursal hasta que le asignes al menos una."
      >
        Sin ninguna sucursal
      </span>
    );
  }
  if (u.alcance_sucursales === 'ASIGNADAS') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2
                       py-0.5 text-xs font-medium text-amber-800 ring-1 ring-amber-200">
        {u.sucursal ? `Solo ${u.sucursal}` : 'Solo las asignadas'}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2
                     py-0.5 text-xs font-medium text-gray-600">
      Todas las sucursales
    </span>
  );
}

const COLS_EXPORT = [
  { etiqueta: 'Nombre', valor: (u) => u.nombre_completo },
  { etiqueta: 'Usuario', valor: (u) => u.email || u.telefono || '' },
  { etiqueta: 'Rol', valor: (u) => etiquetaRol(u.rol) },
  { etiqueta: 'Sucursal', valor: (u) => u.sucursal || '' },
  { etiqueta: 'Alcance', valor: (u) => textoAlcance(u) },
  { etiqueta: 'Estado', valor: (u) => ESTADO_USUARIO[estadoUsuario(u)]?.etiqueta || '' },
  { etiqueta: 'Suspendido hasta', tipo: 'fecha_hora', valor: (u) => u.suspendido_hasta || '' },
  { etiqueta: 'Último acceso', tipo: 'fecha_hora', valor: (u) => u.ultimo_acceso || 'Nunca' },
  { etiqueta: 'Activos a su nombre', valor: (u) => u.activos_resguardados ?? 0 },
];

// Las tres consultas de la pantalla, en un solo sitio. Las sucursales no
// deben tumbar el directorio si fallan: sin ellas se pierde la opción de
// transferir, no la de administrar.
// Los filtros viajan como parámetros; sin ninguno, la ruta devuelve
// exactamente lo que devolvía antes.
const pedirDatos = (filtros = {}) => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(filtros)) {
    if (v !== '' && v !== 0 && v !== false && v != null) q.set(k, v);
  }
  return Promise.all([
    api.get(`/usuarios${q.toString() ? `?${q}` : ''}`),
    // TODAS las invitaciones, no solo las pendientes: una caducada o
    // revocada en gris es información —«a esta persona no le llegó»—, y
    // esconderla hace que se vuelva a invitar a quien ya se invitó.
    api.get('/usuarios/invitaciones?estado='),
    api.get('/sucursales').catch(() => []),
    // CREDENCIALES A3 · quién espera credencial. Si esta consulta falla no
    // se cae la lista: la etiqueta simplemente no aparece.
    api.get('/usuarios/credenciales/pendientes').then((d) => d.pendientes || []).catch(() => []),
    // CORREO F2 · por qué camino se entregó cada invitación (carril accesos).
    api.get('/accesos/invitaciones/entregas').then((d) => d.entregas || []).catch(() => []),
    // L1-6 · el resumen de arriba (contadores + tope del plan) y el
    // catálogo de áreas. Ninguno tumba la lista si falla: sin resumen no
    // se pintan los contadores, sin áreas no se agrupa por área.
    api.get('/usuarios/resumen').catch(() => null),
    api.get('/usuarios/areas').catch(() => []),
  ]);
};

export default function Usuarios() {
  const { usuario: yo } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [invitaciones, setInvitaciones] = useState([]);
  const [esperanCredencial, setEsperanCredencial] = useState({});
  const [entregas, setEntregas] = useState({});
  const [sucursales, setSucursales] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [invitar, setInvitar] = useState(false);
  const [ajustes, setAjustes] = useState(null); // empleado con el panel abierto
  const [codigo, setCodigo] = useState(null);
  const [gafete, setGafete] = useState(null);  // empleado cuyo gafete se mira
  // ACCESOS FASE 2. Los filtros que encuentran a la gente que se pierde
  // en una empresa de cien, y la selección para cambiarles el puesto a
  // todos de una vez.
  // L1-6 · más filtros (estado, sucursal, área) y el orden. El orden
  // arranca en «último acceso», que es el que el ANEXO quiere por
  // omisión, con los «Nunca» al final.
  const [filtros, setFiltros] = useState({ texto: '', rol: '', estado: '',
                                           sucursal: 0, area: 0,
                                           orden: 'ultimo_acceso',
                                           sin_entrar_dias: 0,
                                           contrasena_temporal: false });
  const [resumen, setResumen] = useState(null);
  const [areas, setAreas] = useState([]);
  // Sin agrupar / por sucursal / por área. En una empresa con ocho
  // sucursales, ver ocho bloques en vez de doscientas filas.
  const [agrupacion, setAgrupacion] = useState('ninguna');
  const [seleccion, setSeleccion] = useState(() => new Set());
  const [bloque, setBloque] = useState(false);
  const [importar, setImportar] = useState(false);
  const [gestionarAreas, setGestionarAreas] = useState(false);
  // AC-10: sube cada vez que se aplica algo en bloque, para que la lista de
  // permisos que caducan se vuelva a pedir.
  const [vuelta, setVuelta] = useState(0);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    return pedirDatos(filtros)
      .then(([us, inv, suc, pend, ent, res, ars]) => {
        setUsuarios(us); setInvitaciones(inv); setSucursales(suc);
        setResumen(res); setAreas(ars);
        setEsperanCredencial(Object.fromEntries(pend.map((x) => [x.id_usuario, x])));
        setEntregas(Object.fromEntries(ent.map((x) => [x.id, x])));
        // Lo seleccionado que ya no está en pantalla se descarta: aplicar
        // un cambio a alguien que el filtro dejó fuera es justo lo que
        // hace que nadie vuelva a fiarse de la selección múltiple.
        setSeleccion((sel) => new Set(us.filter((u) => sel.has(u.id)).map((u) => u.id)));
      })
      .catch((e) => setError(e.message))
      .finally(() => setCargando(false));
  }, [filtros]);

  useEffect(() => {
    let vigente = true;
    pedirDatos(filtros)
      .then(([us, inv, suc, pend, ent, res, ars]) => {
        if (!vigente) return;
        setUsuarios(us); setInvitaciones(inv); setSucursales(suc);
        setResumen(res); setAreas(ars);
        setEsperanCredencial(Object.fromEntries(pend.map((x) => [x.id_usuario, x])));
        setEntregas(Object.fromEntries(ent.map((x) => [x.id, x])));
      })
      .catch((e) => vigente && setError(e.message));
    return () => { vigente = false; };
  }, [filtros]);

  // AL VOLVER AL FRENTE, LA LISTA SE ACTUALIZA SOLA · T-14. `useInactividad`
  // ya renovó la credencial y avisó con `tego:volvio-al-frente`; aquí se
  // vuelven a pedir los datos EN SILENCIO —sin el «Cargando…», para no
  // parpadear— para que quien vuelve tras un rato en otra pestaña no mire
  // una foto vieja. Un fallo de red no borra lo que ya se veía: el
  // guardián sigue siendo el servidor.
  useEffect(() => {
    const alVolver = () => {
      pedirDatos(filtros)
        .then(([us, inv, suc, pend, ent, res, ars]) => {
          setUsuarios(us); setInvitaciones(inv); setSucursales(suc);
          setResumen(res); setAreas(ars);
          setEsperanCredencial(Object.fromEntries(pend.map((x) => [x.id_usuario, x])));
          setEntregas(Object.fromEntries(ent.map((x) => [x.id, x])));
        })
        .catch(() => { /* una foto vieja no se borra por un tropiezo de red */ });
    };
    window.addEventListener('tego:volvio-al-frente', alVolver);
    return () => window.removeEventListener('tego:volvio-al-frente', alVolver);
  }, [filtros]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Personal</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Los accesos de tu equipo. Cada cambio queda firmado con tu nombre en la auditoría.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <BotonExportar nombre="personal" columnas={COLS_EXPORT} filas={usuarios} />
          <button
            onClick={() => setImportar(true)}
            className="inline-flex items-center gap-1.5 border border-gray-300 hover:border-gray-400 text-gray-800 text-sm font-semibold px-3 py-2 rounded-lg"
          >
            <Upload className="h-4 w-4" /> Subir una lista
          </button>
          <button
            onClick={() => setInvitar(true)}
            className="inline-flex items-center gap-1.5 bg-[var(--color-marca)] hover:bg-[var(--color-marca-fuerte)] text-white text-sm font-semibold px-3 py-2 rounded-lg"
          >
            <Send className="h-4 w-4" /> Dar de alta
          </button>
        </div>
      </div>

      <NotaExplicativa titulo="¿Cómo entra un empleado, y cómo se le quita el acceso?">
        Para quitarle el acceso hay dos caminos distintos: <strong>suspender</strong> es
        temporal, con días definidos, y el acceso vuelve solo al vencer; <strong>dar de baja</strong> es definitivo
        hasta que alguien lo reactive. Ninguno borra nada: su historial de movimientos se conserva siempre.
        {' '}Y <strong>nadie se va con equipo a su nombre</strong>: si tiene activos resguardados, la baja se
        bloquea hasta que los devuelva, se transfieran o se registren como perdidos.
      </NotaExplicativa>

      {/* L1-5 · el tope del plan, en texto llano. Al llegar al límite el
          botón de nueva cuenta NO se apaga: esto explica el tope y el alta
          ofrece subir de plan. Es un punto de venta, no un muro. */}
      <BannerTope tope={resumen?.tope} />

      {/* L1-2 · los cuatro contadores de toda la empresa, que además son
          filtro rápido: al tocar uno se filtra por ese estado. */}
      <ContadoresPersonal resumen={resumen} filtros={filtros} setFiltros={setFiltros} />

      {/* LOS FILTROS QUE ENCUENTRAN A QUIEN SE PIERDE · Accesos Fase 2.
          Los dos de la derecha no son cosméticos: «sin entrar en 30 días»
          suele ser alguien que ya no trabaja aquí y cuya cuenta sigue
          abierta, y «con contraseña temporal» es alguien cuya clave
          conoce todavía quien se la dio. */}
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[12rem] flex-1">
          <label htmlFor="buscar-persona" className="block text-xs font-semibold text-gray-700 mb-1">
            Buscar por nombre o usuario
          </label>
          <input
            id="buscar-persona" type="search" value={filtros.texto}
            placeholder="lópez, jlopez…"
            onChange={(e) => setFiltros((f) => ({ ...f, texto: e.target.value }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label htmlFor="filtro-estado" className="block text-xs font-semibold text-gray-700 mb-1">Estado</label>
          <select id="filtro-estado" value={filtros.estado}
                  onChange={(e) => setFiltros((f) => ({ ...f, estado: e.target.value }))}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
            {ESTADOS_FILTRO.map((e) => (
              <option key={e.valor} value={e.valor}>{e.etiqueta}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="filtro-rol" className="block text-xs font-semibold text-gray-700 mb-1">Puesto</label>
          <select id="filtro-rol" value={filtros.rol}
                  onChange={(e) => setFiltros((f) => ({ ...f, rol: e.target.value }))}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
            <option value="">Todos</option>
            {['DUENO', ...ROLES_INVITABLES].map((r) => (
              <option key={r} value={r}>{etiquetaRol(r)}</option>
            ))}
          </select>
        </div>
        {sucursales.length > 1 && (
          <div>
            <label htmlFor="filtro-sucursal" className="block text-xs font-semibold text-gray-700 mb-1">Sucursal</label>
            <select id="filtro-sucursal" value={filtros.sucursal}
                    onChange={(e) => setFiltros((f) => ({ ...f, sucursal: Number(e.target.value) }))}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
              <option value={0}>Todas</option>
              {sucursales.map((s) => (
                <option key={s.id} value={s.id}>{s.nombre}</option>
              ))}
            </select>
          </div>
        )}
        {areas.length > 0 && (
          <div>
            <label htmlFor="filtro-area" className="block text-xs font-semibold text-gray-700 mb-1">Área</label>
            <select id="filtro-area" value={filtros.area}
                    onChange={(e) => setFiltros((f) => ({ ...f, area: Number(e.target.value) }))}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
              <option value={0}>Todas</option>
              {areas.map((a) => (
                <option key={a.id} value={a.id}>{a.nombre}</option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label htmlFor="filtro-orden" className="block text-xs font-semibold text-gray-700 mb-1">Ordenar por</label>
          <select id="filtro-orden" value={filtros.orden}
                  onChange={(e) => setFiltros((f) => ({ ...f, orden: e.target.value }))}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
            {ORDENES.map((o) => (
              <option key={o.valor} value={o.valor}>{o.etiqueta}</option>
            ))}
          </select>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 py-2">
          <input
            type="checkbox" checked={filtros.sin_entrar_dias === 30}
            onChange={(e) => setFiltros((f) => ({
              ...f, sin_entrar_dias: e.target.checked ? 30 : 0 }))}
          />
          Sin entrar en 30 días
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 py-2">
          <input
            type="checkbox" checked={filtros.contrasena_temporal}
            onChange={(e) => setFiltros((f) => ({
              ...f, contrasena_temporal: e.target.checked }))}
          />
          Con contraseña temporal sin cambiar
        </label>
      </div>

      {/* CHIPS de lo que está filtrado, cada uno se quita solo, más
          «Limpiar todo». Nada de filtros escondidos que no se ven. */}
      <ChipsFiltros filtros={filtros} setFiltros={setFiltros}
                    sucursales={sucursales} areas={areas} />

      {/* AGRUPAR la lista + gestionar el catálogo de áreas. */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-700">Agrupar:</span>
          {[['ninguna', 'Sin agrupar'], ['sucursal', 'Por sucursal'], ['area', 'Por área']].map(([v, txt]) => (
            <button
              key={v} type="button" onClick={() => setAgrupacion(v)}
              aria-pressed={agrupacion === v}
              className={`text-xs font-semibold px-2.5 py-1 rounded-lg border ${
                agrupacion === v
                  ? 'border-[var(--color-marca)] bg-blue-50 text-[var(--color-marca)]'
                  : 'border-gray-300 text-gray-700 hover:border-gray-400'}`}
            >
              {txt}
            </button>
          ))}
        </div>
        {yo?.rol && JERARQUIA[yo.rol] >= JERARQUIA.ADMIN && (
          <button
            type="button" onClick={() => setGestionarAreas(true)}
            className="text-xs font-semibold text-[var(--color-marca)] hover:underline"
          >
            Gestionar áreas
          </button>
        )}
      </div>

      {/* LA BARRA DE SELECCION SOLO APARECE CUANDO HAY ALGO SELECCIONADO,
          y dice CUÁNTAS personas van a cambiar antes de pulsar nada. */}
      {seleccion.size > 0 && (
        <div role="status"
             className="flex flex-wrap items-center gap-3 rounded-lg border
                        border-[var(--color-marca)] bg-blue-50 px-4 py-2.5">
          <span className="text-sm font-semibold text-gray-900">
            {seleccion.size} persona{seleccion.size === 1 ? '' : 's'} seleccionada{seleccion.size === 1 ? '' : 's'}
          </span>
          <button
            type="button" onClick={() => setBloque(true)}
            className="text-sm font-semibold text-white bg-[var(--color-marca)]
                       px-3 py-1.5 rounded-lg"
          >
            Acciones para los seleccionados
          </button>
          <button
            type="button" onClick={() => setSeleccion(new Set())}
            className="text-sm font-semibold text-gray-600 hover:text-gray-900"
          >
            Quitar la selección
          </button>
        </div>
      )}

      {/* Los tres estados no se pisan: el error NO se enseña junto a
          «Todavía no hay empleados» (un tropiezo de red no es una empresa
          sin gente). Trae su motivo en español y el botón de reintentar. */}
      {error && !cargando && (
        <ErrorConReintento
          que="No se pudo cargar el personal"
          porque={motivoDeError(error)}
          onReintentar={cargar}
        />
      )}
      {cargando && <Cargando filas={4} />}

      {!cargando && !error && usuarios.length === 0 && (
        <EstadoVacio icono={Users} titulo="Todavía no hay empleados" descripcion="Invita a tu primer empleado con “Invitar empleado”." />
      )}

      {/* `min-w-0` en cada tarjeta (EX-4): sin él, la matriz de permisos
          —que es una tabla— obliga a la rejilla a crecer y en teléfono la
          página pasaba a 581 px. Con él, la tabla se desplaza dentro.
          Agrupado, cada bloque trae su encabezado con el conteo. */}
      {agrupar(usuarios, agrupacion).map((grupo) => {
        const rejilla = (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 [&>*]:min-w-0">
            {grupo.filas.map((u) => (
              <Tarjeta
                key={u.id} u={u} yo={yo}
                espera={esperanCredencial[u.id]}
                seleccionada={seleccion.has(u.id)}
                onSeleccionar={administrable(yo, u) ? () => setSeleccion((sel) => {
                  const s2 = new Set(sel);
                  if (s2.has(u.id)) s2.delete(u.id); else s2.add(u.id);
                  return s2;
                }) : null}
                onAjustes={() => setAjustes(u)} onGafete={() => setGafete(u)} />
            ))}
          </div>
        );
        if (grupo.clave === '__todo__') return <div key="__todo__">{rejilla}</div>;
        return (
          <div key={grupo.clave} className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-1">
              {grupo.titulo}{' '}
              <span className="font-normal text-gray-500">
                ({grupo.filas.length} persona{grupo.filas.length === 1 ? '' : 's'})
              </span>
            </h3>
            {rejilla}
          </div>
        );
      })}

      {/* AC-10 · los permisos dados aparte que caducan pronto. Solo la ve
          quien administra; a los demás la ruta les dice que no y no se pinta. */}
      <PermisosQueCaducan vuelta={vuelta} />

      {invitaciones.length > 0 && (
        <ListaEstandar
          titulo="Invitaciones"
          subtitulo="Quién está invitado y en qué va. Las caducadas y las retiradas se quedan a la vista: saber que a alguien no le llegó evita volver a invitarlo por tercera vez."
          columnas={[
            { etiqueta: 'Nombre', valor: (i) => i.nombre_completo, clase: 'font-medium text-gray-900' },
            { etiqueta: 'Usuario o teléfono', valor: (i) => i.nombre_usuario || i.telefono, clase: 'font-mono text-xs' },
            { etiqueta: 'Rol', valor: (i) => etiquetaRol(i.rol) },
            /* `estado_visible` Y NO `estado`. La columna de la base sigue
               diciendo «pendiente» de una invitación que caducó hace tres
               días —solo se actualiza cuando alguien la toca—, y pintarla
               así sería esperar a alguien que no puede entrar. */
            { etiqueta: 'Estado', valor: (i) => i.estado_visible || i.estado,
              render: (i) => <ChipEstado mapa={ESTADO_INVITACION} clave={(i.estado_visible || i.estado).toLowerCase()} /> },
            { etiqueta: 'Creada', tipo: 'fecha_hora', valor: (i) => i.creado_en, render: (i) => <TiempoRelativo valor={i.creado_en} /> },
            // CORREO F2: por qué camino se le entregó, y cuántas veces.
            { etiqueta: 'Entrega', valor: (i) => etiquetaEntrega(entregas[i.id]) },
          ]}
          filas={invitaciones}
          exportar={{ nombre: 'invitaciones' }}
          vacio={null}
          claveFila={(i) => i.id}
        />
      )}

      {/* R-USR-SIST · LAS CUENTAS DE SISTEMA, en su propia sección: bots e
          integraciones, marcadas aparte de las personas. No consumen
          licencia y no entran a la app como personas. */}
      <CuentasSistema yo={yo} />

      {invitar && (
        <ModalInvitar
          sucursales={sucursales}
          onCerrar={() => setInvitar(false)}
          onCreada={(res) => { setInvitar(false); setCodigo(res); cargar(); }}
        />
      )}
      {ajustes && (
        <PanelAjustes
          empleado={ajustes}
          yo={yo}
          sucursales={sucursales}
          onCerrar={() => setAjustes(null)}
          onAplicado={() => { setAjustes(null); cargar(); }}
        />
      )}
      {bloque && (
        <ModalBloque
          ids={[...seleccion]}
          nombres={usuarios.filter((u) => seleccion.has(u.id)).map((u) => u.nombre_completo)}
          areas={areas}
          onCerrar={() => setBloque(false)}
          onAplicado={() => { setBloque(false); setSeleccion(new Set()); setVuelta((v) => v + 1); cargar(); }}
        />
      )}
      {gestionarAreas && (
        <ModalAreas
          onCerrar={() => setGestionarAreas(false)}
          onCambio={cargar}
        />
      )}
      {importar && (
        <ModalImportarPersonas
          onCerrar={() => setImportar(false)}
          onCreadas={(res) => { setImportar(false); setCodigo({ ...res, forma: 'lista' }); cargar(); }}
        />
      )}
      {codigo && <ModalCodigo datos={codigo} onCerrar={() => setCodigo(null)} />}
      {gafete && <ModalGafete empleado={gafete} onCerrar={() => setGafete(null)} />}
    </div>
  );
}

// ¿Puedo administrar a esta persona? Es el espejo exacto de las reglas 1 y 2
// del backend (app/routers/usuarios.py). Ocultar el botón es comodidad; la
// que manda es la comprobación del servidor.
function administrable(yo, u) {
  if (!yo || u.id === yo.id) return false;
  return (JERARQUIA[u.rol] || 0) < (JERARQUIA[yo.rol] || 0);
}

// L1-6 · AGRUPA la lista sin agrupar / por sucursal / por área. Devuelve
// bloques {clave, titulo, filas}. «Sin agrupar» es un solo bloque especial
// que se pinta sin encabezado. Los que no tienen sucursal o área caen en
// un bloque «Sin asignar» al final, para que no desaparezcan de la vista.
function agrupar(usuarios, modo) {
  if (modo !== 'sucursal' && modo !== 'area') {
    return [{ clave: '__todo__', titulo: '', filas: usuarios }];
  }
  const campo = modo === 'sucursal' ? 'sucursal' : 'area';
  const bloques = new Map();
  for (const u of usuarios) {
    const nombre = u[campo] || null;
    const clave = nombre ?? '__sin__';
    if (!bloques.has(clave)) {
      bloques.set(clave, {
        clave,
        titulo: nombre || (modo === 'sucursal' ? 'Sin sucursal' : 'Sin área'),
        orden: nombre ? 0 : 1,
        filas: [],
      });
    }
    bloques.get(clave).filas.push(u);
  }
  return [...bloques.values()].sort(
    (a, b) => a.orden - b.orden || a.titulo.localeCompare(b.titulo));
}

// L1-5 · EL TOPE DEL PLAN, en texto llano. Si no hay tope (la nube no bajó
// ninguno) no se pinta nada: no se inventa un límite que no existe.
function BannerTope({ tope }) {
  if (!tope || tope.tope == null) return null;
  const { usados, tope: max, plan_nombre } = tope;
  const lleno = usados >= max;
  const plan = plan_nombre ? ` de tu plan ${plan_nombre}` : '';
  return (
    <div
      role="status"
      className={`text-sm rounded-lg border px-4 py-2.5 ${
        lleno
          ? 'border-amber-300 bg-amber-50 text-amber-900'
          : 'border-gray-200 bg-gray-50 text-gray-700'}`}
    >
      Estás usando <strong>{usados}</strong> de las <strong>{max}</strong> cuentas{plan}.
      {lleno && (
        <>
          {' '}Llegaste al límite del plan. Puedes dar de alta a más personas
          subiendo de plan; lo que ya tienes se queda igual.
        </>
      )}
    </div>
  );
}

// L1-2 · LOS CUATRO CONTADORES, que además son filtro rápido. El que está
// puesto se resalta; tocarlo otra vez lo quita. Verde=bien, rojo=problema,
// gris=trabajo pendiente, con el mismo código de color de todo el sistema.
function ContadoresPersonal({ resumen, filtros, setFiltros }) {
  if (!resumen) return null;
  const tarjetas = [
    { estado: '', etiqueta: 'En total', n: resumen.total, color: 'gris' },
    { estado: 'activos', etiqueta: 'Activos', n: resumen.activos, color: 'verde' },
    { estado: 'bloqueados', etiqueta: 'Bloqueados', n: resumen.bloqueados, color: 'rojo' },
    { estado: 'nunca', etiqueta: 'Nunca han entrado', n: resumen.sin_entrar, color: 'gris' },
  ];
  const tono = {
    verde: 'text-green-700', rojo: 'text-red-700', gris: 'text-gray-800',
  };
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {tarjetas.map((t) => {
        const activo = filtros.estado === t.estado;
        return (
          <button
            key={t.etiqueta || 'total'} type="button"
            aria-pressed={activo}
            onClick={() => setFiltros((f) => ({
              ...f, estado: activo && t.estado ? '' : t.estado }))}
            className={`text-left rounded-lg border px-3 py-2 ${
              activo
                ? 'border-[var(--color-marca)] bg-blue-50'
                : 'border-gray-200 bg-white hover:border-gray-300'}`}
          >
            <div className={`text-xl font-bold ${tono[t.color]}`}>{t.n}</div>
            <div className="text-xs text-gray-600">{t.etiqueta}</div>
          </button>
        );
      })}
    </div>
  );
}

// L1-1 · LOS CHIPS de lo que está filtrado, cada uno removible, más
// «Limpiar todo». Nada de filtros escondidos que el usuario no sabe que
// están puestos.
function ChipsFiltros({ filtros, setFiltros, sucursales, areas }) {
  const quitar = (cambio) => setFiltros((f) => ({ ...f, ...cambio }));
  const chips = [];
  if (filtros.texto)
    chips.push({ k: 'texto', txt: `Buscando: “${filtros.texto}”`, quita: { texto: '' } });
  if (filtros.estado) {
    const e = ESTADOS_FILTRO.find((x) => x.valor === filtros.estado);
    chips.push({ k: 'estado', txt: `Estado: ${e?.etiqueta || filtros.estado}`, quita: { estado: '' } });
  }
  if (filtros.rol)
    chips.push({ k: 'rol', txt: `Puesto: ${etiquetaRol(filtros.rol)}`, quita: { rol: '' } });
  if (filtros.sucursal) {
    const s = sucursales.find((x) => x.id === filtros.sucursal);
    chips.push({ k: 'sucursal', txt: `Sucursal: ${s?.nombre || filtros.sucursal}`, quita: { sucursal: 0 } });
  }
  if (filtros.area) {
    const a = areas.find((x) => x.id === filtros.area);
    chips.push({ k: 'area', txt: `Área: ${a?.nombre || filtros.area}`, quita: { area: 0 } });
  }
  if (filtros.sin_entrar_dias)
    chips.push({ k: 'sed', txt: 'Sin entrar en 30 días', quita: { sin_entrar_dias: 0 } });
  if (filtros.contrasena_temporal)
    chips.push({ k: 'ct', txt: 'Con contraseña temporal', quita: { contrasena_temporal: false } });

  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {chips.map((c) => (
        <span key={c.k}
              className="inline-flex items-center gap-1 text-xs font-medium
                         bg-gray-100 text-gray-700 rounded-full pl-3 pr-1 py-1">
          {c.txt}
          <button
            type="button" onClick={() => quitar(c.quita)}
            aria-label={`Quitar filtro ${c.txt}`}
            className="rounded-full w-5 h-5 flex items-center justify-center
                       text-gray-500 hover:bg-gray-200 hover:text-gray-800"
          >
            ×
          </button>
        </span>
      ))}
      <button
        type="button"
        onClick={() => setFiltros((f) => ({
          ...f, texto: '', estado: '', rol: '', sucursal: 0, area: 0,
          sin_entrar_dias: 0, contrasena_temporal: false }))}
        className="text-xs font-semibold text-[var(--color-marca)] hover:underline ml-1"
      >
        Limpiar todo
      </button>
    </div>
  );
}

// ¿PUEDO IMPRIMIR EL GAFETE DE ESTA PERSONA?
//
// ES OTRA REGLA, Y POR ESO ES OTRA FUNCIÓN. `administrable` prohíbe
// tocarse a uno mismo, y con razón: subirse el rango o quitarse una
// restricción no es gestión, es escalada de privilegios.
//
// Un gafete no es ninguna de las dos cosas: es la identidad que ya
// tienes, impresa en un cartón. **Imprimir el propio es el caso normal**
// —y con la regla de `administrable`, el día que el administrador esté
// de vacaciones no entra nadie a la planta.
//
// Lo que sí se conserva es «solo hacia abajo»: un gafete abre una
// terminal A NOMBRE de quien lo lleva, así que imprimir el del jefe sería
// poder mover inventario y que el historial dijera —con toda la razón—
// que lo hizo él. Es el espejo exacto de `_abrir_gafete` en el backend, y
// la que manda es la del servidor.
// CORREO F2 · la etiqueta de entrega de una invitación, desde lo que anotó accesos.
const NOMBRE_ENTREGA = { CORREO: 'Enviada por correo', HOJA: 'Hoja impresa', BOT: 'Por el bot', PANTALLA: 'Dictada' };
function etiquetaEntrega(e) {
  if (!e || !e.entregada_por) return 'Sin entregar';
  const veces = (e.reenvios || 0) + (e.impresiones || 0);
  return `${NOMBRE_ENTREGA[e.entregada_por] || e.entregada_por}${veces > 1 ? ` ×${veces}` : ''}`;
}

function puedeGafete(yo, u) {
  if (!yo) return false;
  if (u.id === yo.id) return true;
  return (JERARQUIA[u.rol] || 0) < (JERARQUIA[yo.rol] || 0);
}

// CREDENCIALES A3 · qué se le dice a quien administra según el estado
// DERIVADO de la entrega viva. Nunca se calcula aquí: viene de la base.
const ESPERA_CREDENCIAL = {
  PENDIENTE: { texto: 'Espera credencial: emitida y todavía no entregada.', clase: 'text-amber-800 bg-amber-50 border-amber-200' },
  ENTREGADA: { texto: 'Espera credencial: ya la tiene y todavía no fija su contraseña.', clase: 'text-amber-800 bg-amber-50 border-amber-200' },
  EXPIRADA: { texto: 'Su credencial caducó sin usarse. Emítele otra desde «Restablecer contraseña».', clase: 'text-red-800 bg-red-50 border-red-200' },
};

function Tarjeta({ u, yo, espera, onAjustes, onGafete, seleccionada, onSeleccionar }) {
  const puedo = administrable(yo, u);
  const puedoGafete = puedeGafete(yo, u);
  const clave = estadoUsuario(u);

  return (
    <div className={`bg-white border rounded-xl p-4 space-y-3 ${
      seleccionada ? 'border-[var(--color-marca)] ring-1 ring-[var(--color-marca)]'
                   : 'border-gray-200'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-2">
          {/* LA CASILLA SOLO EXISTE SI SE PUEDE ADMINISTRAR A ESA PERSONA.
              Una casilla que se marca y luego el servidor rechaza es peor
              que no tenerla: enseña que la selección no significa nada. */}
          {onSeleccionar && (
            <input
              type="checkbox" checked={seleccionada} onChange={onSeleccionar}
              aria-label={`Seleccionar a ${u.nombre_completo}`}
              className="mt-1 h-4 w-4 shrink-0"
            />
          )}
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 truncate">{u.nombre_completo}</p>
            <p className="text-xs text-gray-500 font-mono truncate">
              {u.nombre_usuario || u.email || u.telefono || '—'}
            </p>
          </div>
        </div>
        <ChipEstado mapa={ESTADO_USUARIO} clave={clave} />
      </div>

      {/* QUIEN TODAVIA NO CAMBIO SU CONTRASEÑA TEMPORAL. No es un error
          —acaban de darla de alta— pero sí algo que hay que ver: mientras
          no la cambie, su clave la conoce quien se la dio. Ámbar, que es
          «atención», no rojo. */}
      {espera && ESPERA_CREDENCIAL[espera.estado] ? (
        <p className={`text-xs border rounded p-2 ${ESPERA_CREDENCIAL[espera.estado].clase}`}>
          {ESPERA_CREDENCIAL[espera.estado].texto}
        </p>
      ) : u.debe_cambiar_password && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
          No ha cambiado su contraseña temporal. Hasta que lo haga, su
          clave la conoce quien se la entregó.
        </p>
      )}

      <dl className="text-sm space-y-1">
        <Dato campo="Rol" valor={etiquetaRol(u.rol)} />
        <Dato campo="Sucursal" valor={u.sucursal || '—'} />
        {u.area && <Dato campo="Área" valor={u.area} />}
        <Dato campo="Alcance" valor={<ChipAlcance u={u} />} />
        {u.sin_ninguna_sucursal && (
          <p className="mt-2 rounded-md bg-red-50 p-2 text-xs text-red-800
                        ring-1 ring-red-200">
            Esta persona <strong>no verá ninguna sucursal</strong>: está
            limitada a las que se le asignen y no tiene ninguna. Asígnale al
            menos una, o cámbiale el alcance a todas las sucursales.
          </p>
        )}
        <Dato campo="Último acceso" valor={<TiempoRelativo valor={u.ultimo_acceso} vacio="Nunca" />} />
        {/* Se enseña ANTES de intentar la baja: descubrir la regla al
            chocar con ella es la peor forma de aprenderla. */}
        {u.activos_resguardados > 0 && (
          <Dato
            campo="Activos a su nombre"
            valor={
              <span className="text-amber-700 font-semibold">
                {u.activos_resguardados}
              </span>
            }
          />
        )}
        {clave === 'suspendido' && (
          <Dato campo="Vuelve" valor={<TiempoRelativo valor={u.suspendido_hasta} vacio="—" />} />
        )}
      </dl>

      <Permisos empleado={u} puedeEditar={administrable(yo, u)} />
      {/* LA TARJETA NFC (DIRECTRIZ-TERMINAL-NFC N3, parche del integrador en el
          merge): darla, verla y quitarla desde aquí; la baja la revoca sola. */}
      <TarjetasDePersona idUsuario={u.id} personaActiva={u.activo !== false} puedeEditar={administrable(yo, u)} />
      {/* DIRECTRIZ-LLAVE L-3 (R-LL-3): el Admin Supremo ve y QUITA, con
          motivo, las llaves de acceso de otra persona. Nunca las crea. */}
      {veLlavesDeOtros(yo, u) && <LlavesDeUnaPersona persona={u} />}
      {/* EL EXPEDIENTE DE LA PERSONA (DIRECTRIZ-ARCHIVOS C, parche del
          integrador en el merge): se crea con el primer documento y dice
          qué le falta; con la baja queda en solo lectura. */}
      <Expediente tipo="persona" referencia={u.id} />

      {u.activos_resguardados > 0 && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
          Tiene {u.activos_resguardados} activo(s) de la empresa. No se le puede
          dar de baja hasta que los devuelva, se transfieran a otra persona o se
          registren como perdidos.
        </p>
      )}

      {clave === 'suspendido' && u.motivo_suspension && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">{u.motivo_suspension}</p>
      )}

      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={onAjustes}
          disabled={!puedo}
          title={puedo ? undefined : 'Solo puedes administrar cuentas de rango menor al tuyo, y nunca la tuya.'}
          className="inline-flex items-center justify-center gap-1.5 border border-gray-300 bg-white text-gray-700 text-sm font-semibold px-3 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-40"
        >
          <Settings2 className="h-4 w-4" /> Ajustes
        </button>
        {/* EL GAFETE VA APARTE Y NO DENTRO DE «AJUSTES», y no es una
            preferencia de maquetación: los ajustes son cosas que se le
            hacen A otra persona (subirle el rango, suspenderla) y el
            gafete es algo que también te haces a ti. Meterlo ahí dentro
            obligaría a que el botón de ajustes se habilitara para uno
            mismo, y entonces habría que explicar en cada opción a cuál de
            las dos reglas obedece. */}
        <button
          onClick={onGafete}
          disabled={!puedoGafete || !u.activo}
          title={
            !u.activo
              ? 'Esta persona está dada de baja: un gafete suyo no abriría ninguna terminal.'
              : puedoGafete
                ? undefined
                : 'Un gafete abre una terminal a nombre de quien lo lleva, así que solo puedes imprimir el tuyo o el de alguien de rango menor.'
          }
          className="inline-flex items-center justify-center gap-1.5 border border-gray-300 bg-white text-gray-700 text-sm font-semibold px-3 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-40"
        >
          <IdCard className="h-4 w-4" /> Gafete
        </button>
      </div>
    </div>
  );
}

function Dato({ campo, valor }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="text-gray-500">{campo}</dt>
      <dd className="text-gray-900 text-right truncate">{valor}</dd>
    </div>
  );
}

// Panel de ajustes: elige la acción, y cada una abre su modal de
// confirmación inmutable con el cambio exacto y el motivo obligatorio.
// PERMISOS DE UNA PERSONA — lo que trae su puesto y lo que se le dio aparte.
//
// LA PIEZA 3, y sin ella el modelo existía pero el dueño no lo podía usar:
// hasta hoy un permiso individual se concedía con un INSERT a mano.
//
// LAS DOS LISTAS VAN SEPARADAS a propósito. Mezcladas, un permiso
// excepcional —«a este encargado le dimos el valor a costo»— se pierde
// entre los quince que trae su puesto, que es justo lo que hay que poder
// revisar seis meses después.
function Permisos({ empleado: u, puedeEditar }) {
  const [datos, setDatos] = useState(null);
  const [catalogo, setCatalogo] = useState([]);
  const [abriendo, setAbriendo] = useState(false);
  const [elegido, setElegido] = useState('');
  const [motivo, setMotivo] = useState('');
  const [hasta, setHasta] = useState('');
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  // `useCallback` y no una función suelta: sin él, `recargar` es nueva en
  // cada render y el `useEffect` que la usa se dispararía en bucle.
  const recargar = useCallback(
    () => api.get(`/permisos/${u.id}`).then(setDatos).catch(() => setDatos(null)),
    [u.id],
  );

  // ANEXO-CLIENTE-01 H-4 (20 sep 2026): las plantillas de oficio, servidas
  // por el carril `experiencia`. Si la ruta no está, la matriz sigue igual.
  const [plantillas, setPlantillas] = useState([]);

  useEffect(() => {
    recargar();
    if (puedeEditar) {
      api.get('/permisos/catalogo').then(setCatalogo).catch(() => {});
      api.get('/experiencia/plantillas-de-permisos').then(setPlantillas).catch(() => {});
    }
  }, [recargar, puedeEditar]);

  if (!datos) return null;

  // Solo se ofrece lo que NO trae ya su puesto: ofrecer algo que ya tiene
  // es una trampa, y el servidor lo rechazaría de todos modos.
  const yaTiene = new Set([
    ...(datos.por_rol || []).map((p) => p.codigo),
    ...(datos.concedidos || []).map((p) => p.codigo),
  ]);
  const ofrecibles = catalogo.filter((p) => !yaTiene.has(p.codigo));

  const conceder = async () => {
    setError('');
    setGuardando(true);
    try {
      await api.post(`/permisos/${u.id}`, {
        permiso: elegido,
        motivo,
        vigente_hasta: hasta || null,
      });
      setAbriendo(false);
      setElegido(''); setMotivo(''); setHasta('');
      await recargar();
    } catch (e) {
      setError(e?.detalle || 'No se pudo conceder el permiso.');
    } finally {
      setGuardando(false);
    }
  };

  const quitar = async (codigo) => {
    try {
      await api.del(`/permisos/${u.id}/${codigo}`);
      await recargar();
    } catch (e) {
      setError(e?.detalle || 'No se pudo quitar el permiso.');
    }
  };

  return (
    <section className="mt-4 border-t pt-3">
      <h4 className="text-sm font-semibold text-gray-800">Permisos</h4>

      {/* LA MATRIZ (EXPERIENCIA E4): medido el 16 sep 2026, aquí había un
          párrafo de hasta 3 591 caracteres. Ahora: resumen en una línea,
          búsqueda, y una fila por módulo con ver / editar / eliminar /
          autorizar. Lo del puesto va con candado; lo concedido aparte se
          quita desde la propia casilla; lo que falta se da con su motivo
          por el mismo `/api/permisos` de siempre. */}
      <div className="mt-2">
        <MatrizDePermisos
          catalogo={catalogo}
          porRol={datos.por_rol || []}
          concedidos={datos.concedidos || []}
          esSupremo={u.rol === 'DUENO'}
          puedeEditar={puedeEditar}
          onConceder={async (codigo, porQue) => {
            setError('');
            try {
              await api.post(`/permisos/${u.id}`, { permiso: codigo, motivo: porQue, vigente_hasta: null });
              await recargar();
            } catch (e) { setError(e?.detalle || 'No se pudo conceder el permiso.'); }
          }}
          onQuitar={quitar}
          plantillas={plantillas}
          onPlantilla={async (clave) => {
            setError('');
            const r = await api.post(`/experiencia/plantillas-de-permisos/${clave}/aplicar/${u.id}`);
            await recargar();
            return r;
          }}
        />
      </div>

      <details className="mt-3 text-xs text-gray-700">
        <summary className="cursor-pointer select-none font-medium">Concedidos aparte, con quién y por qué</summary>
      <div className="mt-1">
        {(datos.concedidos || []).length === 0 ? (
          <p className="mt-1 text-xs text-gray-600">
            Ninguno. Solo tiene lo de su puesto.
          </p>
        ) : (
          <ul className="mt-1 space-y-2">
            {(datos.concedidos || []).map((p) => (
              <li
                key={p.codigo}
                className={`rounded-md p-2 text-xs ring-1 ${
                  p.caducado
                    ? 'bg-gray-50 text-gray-500 ring-gray-200'
                    : 'bg-amber-50 text-amber-900 ring-amber-200'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <span className="font-medium">{p.nombre}</span>
                    {p.caducado && <span className="ml-1">· caducado</span>}
                    <p className="mt-0.5">{p.motivo}</p>
                    {/* QUIÉN SE LO DIO Y CUÁNDO. Sin eso, un permiso suelto
                        es imposible de revisar después. */}
                    <p className="mt-0.5 opacity-75">
                      Se lo dio {p.concedido_por || 'alguien que ya no está'}
                      {p.concedido_en
                        ? ` el ${String(p.concedido_en).slice(0, 10)}`
                        : ''}
                      {p.vigente_hasta ? ` · hasta el ${p.vigente_hasta}` : ''}
                    </p>
                  </div>
                  {puedeEditar && (
                    <button
                      type="button"
                      onClick={() => quitar(p.codigo)}
                      className="shrink-0 rounded border border-current px-2 py-0.5"
                    >
                      Quitar
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      </details>

      {error && <p className="mt-2 text-xs text-red-700">{error}</p>}

      {puedeEditar && !abriendo && ofrecibles.length > 0 && (
        <button
          type="button"
          onClick={() => setAbriendo(true)}
          className="mt-3 rounded border px-2 py-1 text-xs"
        >
          Conceder un permiso
        </button>
      )}

      {puedeEditar && abriendo && (
        <div className="mt-3 space-y-2 rounded-md bg-gray-50 p-2">
          <label className="block text-xs">
            Qué permiso
            <select
              value={elegido}
              onChange={(e) => setElegido(e.target.value)}
              className="mt-0.5 w-full rounded border p-1 text-xs"
            >
              <option value="">Elige uno…</option>
              {ofrecibles.map((p) => (
                <option key={p.codigo} value={p.codigo}>
                  {p.nombre} — {p.descripcion}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-xs">
            Por qué se lo das
            <input
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Lleva la tienda grande y cotiza compras"
              className="mt-0.5 w-full rounded border p-1 text-xs"
            />
            <span className="text-[10px] text-gray-500">
              Se queda escrito. Dentro de seis meses, esto es lo único que
              explicará por qué lo tiene.
            </span>
          </label>

          <label className="block text-xs">
            Hasta cuándo (opcional)
            <input
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              className="mt-0.5 w-full rounded border p-1 text-xs"
            />
            <span className="text-[10px] text-gray-500">
              Si lo dejas vacío no caduca. Con fecha, vuelve solo a su sitio
              y no hay que acordarse de quitarlo.
            </span>
          </label>

          <div className="flex gap-2">
            <button
              type="button"
              disabled={!elegido || motivo.trim().length < 4 || guardando}
              aria-describedby={!elegido || motivo.trim().length < 4 ? 'motivo-conceder' : undefined}
              onClick={conceder}
              className="rounded bg-gray-900 px-2 py-1 text-xs text-white
                         disabled:opacity-40"
            >
              {guardando ? 'Concediendo…' : 'Conceder'}
            </button>
            {/* BOT-1 (19 sep 2026): por qué está gris, a la vista. */}
            <Motivo id="motivo-conceder" cuando={!guardando && (!elegido || motivo.trim().length < 4)} className="self-center">
              {!elegido ? 'Elige el permiso que vas a conceder.' : 'Escribe el motivo (mínimo 4 letras): queda en la bitácora.'}
            </Motivo>
            <button
              type="button"
              onClick={() => { setAbriendo(false); setError(''); }}
              className="rounded border px-2 py-1 text-xs"
            >
              Cancelar
            </button>
          </div>
          {(!elegido || motivo.trim().length < 4) && (
            <p className="text-[10px] text-gray-500">
              Elige un permiso y escribe por qué se lo das.
            </p>
          )}
        </div>
      )}
    </section>
  );
}


function PanelAjustes({ empleado: u, yo, sucursales, onCerrar, onAplicado }) {
  const [accion, setAccion] = useState(null);
  const suspendido = estadoUsuario(u) === 'suspendido';

  const opciones = [
    { id: 'rol', etiqueta: 'Cambiar de rango', descripcion: 'Sube o baja el rol del empleado.' },
    ...(u.activo && !suspendido
      ? [{ id: 'suspender', etiqueta: 'Suspender temporalmente', descripcion: 'Con días definidos; el acceso vuelve solo al vencer.', peligro: true }]
      : [{ id: 'reactivar', etiqueta: 'Devolver el acceso', descripcion: 'Levanta la suspensión y reactiva la cuenta.' }]),
    ...(u.activo
      ? [{ id: 'desactivar', etiqueta: 'Dar de baja', descripcion: 'Pierde el acceso hasta que alguien lo reactive. No borra su historial.', peligro: true }]
      : []),
    ...(sucursales.length > 1 ? [{ id: 'sucursal', etiqueta: 'Transferir de sucursal', descripcion: 'Traslada al empleado y le retira el acceso a la anterior.' }] : []),
    { id: 'restablecer', etiqueta: 'Restablecer acceso', descripcion: 'Desbloquea la cuenta tras varios intentos fallidos.' },
    // SUPREMO A4 · el puesto es un catálogo; lo cambian ADMIN y Supremo.
    { id: 'puesto', etiqueta: 'Cambiar de puesto', descripcion: 'Elige el puesto del catálogo de tu empresa. La constancia DC-3 lo imprime tal cual.' },
    // SUPREMO A2 · solo el Supremo, y solo sobre otra persona.
    ...(yo?.rol === 'DUENO' && u.activo && u.rol !== 'DISPOSITIVO'
      ? [{ id: 'traspasar', etiqueta: 'Traspasar el rol de Admin Supremo', descripcion: 'Esta persona pasa a ser el Admin Supremo y tú quedas como administrador. Con tu contraseña.', peligro: true }]
      : []),
    // CREDENCIALES A2 · una contraseña temporal nueva, por el canal que
    // exista de verdad para esa persona. Solo si tengo el permiso.
    ...(yo?.permisos?.includes?.('usuarios.restablecer_contrasena') || ['ADMIN', 'DUENO'].includes(yo?.rol)
      ? [
        { id: 'contrasena', etiqueta: 'Restablecer contraseña', descripcion: 'Le da una contraseña temporal nueva por Telegram, correo o en pantalla. La anterior deja de servir y sus sesiones se cierran.' },
        { id: 'credenciales', etiqueta: 'Credenciales entregadas', descripcion: 'Qué credenciales se le han entregado, por dónde y en qué quedó cada una. Desde aquí se reenvían, se revocan y se desvincula Telegram.' },
      ]
      : []),
  ];

  if (accion) {
    return (
      <ModalAccion
        accion={accion}
        empleado={u}
        yo={yo}
        sucursales={sucursales}
        onCerrar={() => setAccion(null)}
        onAplicado={onAplicado}
      />
    );
  }

  return (
    <Modal titulo={`Ajustes de ${u.nombre_completo}`} onCerrar={onCerrar}>
      <div className="space-y-2">
        {opciones.map((o) => (
          <button
            key={o.id}
            onClick={() => setAccion(o)}
            className={`w-full text-left border rounded-lg p-3 hover:bg-gray-50 ${
              o.peligro ? 'border-red-200' : 'border-gray-200'
            }`}
          >
            <p className={`text-sm font-semibold ${o.peligro ? 'text-red-700' : 'text-gray-900'}`}>{o.etiqueta}</p>
            <p className="text-xs text-gray-500 mt-0.5">{o.descripcion}</p>
          </button>
        ))}
      </div>
    </Modal>
  );
}

function ModalAccion({ accion, empleado: u, yo, sucursales, onCerrar, onAplicado }) {
  const rolesPosibles = ROLES_INVITABLES.filter(
    (r) => (JERARQUIA[r] || 0) <= (JERARQUIA[yo?.rol] || 0) && r !== u.rol,
  );
  const [rol, setRol] = useState(rolesPosibles[0] || '');
  const [dias, setDias] = useState(7);
  const [idSucursal, setIdSucursal] = useState(
    String(sucursales.find((s) => s.id !== u.id_sucursal)?.id || ''),
  );

  if (accion.id === 'contrasena') {
    return <ModalRestablecerContrasena empleado={u} onCerrar={onCerrar} onAplicado={onAplicado} />;
  }
  if (accion.id === 'credenciales') {
    return <ModalCredenciales empleado={u} onCerrar={onCerrar} onAplicado={onAplicado} />;
  }
  if (accion.id === 'puesto') {
    return <ModalPuesto empleado={u} onCerrar={onCerrar} onAplicado={onAplicado} />;
  }
  if (accion.id === 'traspasar') {
    return <ModalTraspaso empleado={u} onCerrar={onCerrar} onAplicado={onAplicado} />;
  }

  // Restablecer no tiene motivo ni cambio de estado: se resuelve aparte.
  if (accion.id === 'restablecer') {
    return (
      <ModalConfirmar
        titulo={`Restablecer el acceso de ${u.nombre_completo}`}
        descripcion="Desbloquea la cuenta y limpia los intentos fallidos. Si entra con contraseña, se le pedirá una nueva."
        etiquetaConfirmar="Restablecer"
        onConfirmar={async () => { await api.post(`/usuarios/${u.id}/restablecer`, {}); onAplicado(); }}
        onCerrar={onCerrar}
      />
    );
  }

  const config = {
    rol: {
      titulo: `Cambiar el rango de ${u.nombre_completo}`,
      descripcion: 'El cambio surte efecto en su siguiente petición, sin esperar a que cierre sesión.',
      cambios: [{ campo: 'Rol', antes: etiquetaRol(u.rol), despues: etiquetaRol(rol) }],
      etiqueta: 'Aplicar cambio de rango',
      extra: (
        <CampoValidado etiqueta="Nuevo rol" valor={rol}>
          {(props) => (
            <select {...props} value={rol} onChange={(e) => setRol(e.target.value)}>
              {rolesPosibles.map((r) => <option key={r} value={r}>{etiquetaRol(r)}</option>)}
            </select>
          )}
        </CampoValidado>
      ),
      enviar: (motivo) => api.post(`/usuarios/${u.id}/rol`, { rol, motivo }),
      valido: Boolean(rol),
    },
    suspender: {
      titulo: `Suspender a ${u.nombre_completo}`,
      descripcion: 'Su sesión abierta deja de funcionar de inmediato. El acceso vuelve solo al vencer el plazo.',
      cambios: [{ campo: 'Estado', antes: 'Activo', despues: `Suspendido ${dias} día(s)` }],
      etiqueta: 'Suspender',
      peligro: true,
      extra: (
        <CampoValidado
          etiqueta="Días de suspensión" requerido valor={dias}
          ayuda="El acceso vuelve solo al vencer el plazo; no hay que acordarse de reactivarlo."
          validar={(v) => (v >= 1 && v <= 365 ? null : 'Entre 1 y 365 días.')}
        >
          {(props) => (
            <input {...props} type="number" min={1} max={365} value={dias}
                   onChange={(e) => setDias(Number(e.target.value))} />
          )}
        </CampoValidado>
      ),
      enviar: (motivo) => api.post(`/usuarios/${u.id}/suspender`, { dias, motivo }),
      valido: dias >= 1,
    },
    reactivar: {
      titulo: `Devolver el acceso a ${u.nombre_completo}`,
      descripcion: 'Levanta la suspensión y reactiva la cuenta.',
      cambios: [{ campo: 'Estado', antes: ESTADO_USUARIO[estadoUsuario(u)]?.etiqueta, despues: 'Activo' }],
      etiqueta: 'Devolver el acceso',
      enviar: (motivo) => api.post(`/usuarios/${u.id}/reactivar`, { motivo }),
      valido: true,
    },
    desactivar: {
      titulo: `Dar de baja a ${u.nombre_completo}`,
      descripcion: 'Pierde el acceso hasta que alguien lo reactive. Su historial de movimientos se conserva intacto.',
      cambios: [{ campo: 'Estado', antes: 'Activo', despues: 'Dado de baja' }],
      etiqueta: 'Dar de baja',
      peligro: true,
      palabra: 'BAJA',
      enviar: (motivo) => api.post(`/usuarios/${u.id}/desactivar`, { motivo }),
      valido: true,
    },
    sucursal: {
      titulo: `Transferir a ${u.nombre_completo}`,
      descripcion: 'Se le retira el acceso a la sucursal anterior.',
      cambios: [{
        campo: 'Sucursal',
        antes: u.sucursal || '—',
        despues: sucursales.find((s) => String(s.id) === idSucursal)?.nombre || '—',
      }],
      etiqueta: 'Transferir',
      extra: (
        <CampoValidado
          etiqueta="Nueva sucursal" requerido valor={idSucursal}
          validar={(v) => (v ? null : 'Elige a qué sucursal se transfiere.')}
        >
          {(props) => (
            <select {...props} value={idSucursal} onChange={(e) => setIdSucursal(e.target.value)}>
              {sucursales.filter((s) => s.id !== u.id_sucursal).map((s) => (
                <option key={s.id} value={s.id}>{s.nombre}</option>
              ))}
            </select>
          )}
        </CampoValidado>
      ),
      enviar: (motivo) => api.post(`/usuarios/${u.id}/sucursal`, { id_sucursal: Number(idSucursal), motivo }),
      valido: Boolean(idSucursal),
    },
  }[accion.id];

  return (
    <ModalConfirmar
      titulo={config.titulo}
      descripcion={config.descripcion}
      extra={config.extra}
      cambios={config.cambios}
      pedirMotivo
      palabraConfirmacion={config.palabra}
      peligro={config.peligro}
      etiquetaConfirmar={config.etiqueta}
      onConfirmar={async (motivo) => {
        if (!config.valido) throw new ErrorApi('Completa los datos de la acción.');
        await config.enviar(motivo);
        onAplicado();
      }}
      onCerrar={onCerrar}
    />
  );
}

// =====================================================================
// DAR DE ALTA A ALGUIEN QUE NO TIENE CORREO · Accesos Fase 1
// =====================================================================
// TRES FORMAS, Y LA PANTALLA LAS EXPLICA EN VEZ DE ASUMIR QUE SE SABEN:
//
//   · Por WhatsApp — se le manda un enlace y un código a su teléfono, y
//     ella elige su PIN. Es lo que había y sigue igual.
//   · Con usuario — se le imprime una hoja con su usuario y un código, y
//     ella pone su propia contraseña. Para quien no tiene teléfono.
//   · Alta directa — se le entrega usuario y una contraseña temporal
//     ahí mismo. Para cuando la persona está delante y hay prisa.
//
// LA MEJOR ES LA SEGUNDA Y LA PANTALLA LO DICE, porque en la tercera la
// contraseña la conoce quien la crea hasta que la persona la cambia.
const FORMAS = [
  {
    id: 'whatsapp', titulo: 'Por WhatsApp',
    pie: 'Le llega un código a su teléfono y ella elige su PIN',
    necesita: 'telefono',
  },
  {
    id: 'usuario', titulo: 'Con un usuario',
    pie: 'Le imprimes una hoja y ella pone su contraseña',
    necesita: 'usuario',
  },
  {
    id: 'directa', titulo: 'Alta directa',
    pie: 'Le das usuario y una contraseña temporal ahora mismo',
    necesita: 'usuario',
  },
];

function ModalInvitar({ sucursales, onCerrar, onCreada }) {
  const [forma, setForma] = useState('whatsapp');
  const [f, setF] = useState({
    nombre_completo: '', telefono: '', nombre_usuario: '',
    rol: 'OPERADOR', sucursales_ids: [],
  });
  const [error, setError] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [tocadoSucursales, setTocadoSucursales] = useState(false);

  const pideUsuario = FORMAS.find((x) => x.id === forma).necesita === 'usuario';
  const esDirecta = forma === 'directa';

  // Las mismas reglas del servidor, dichas al salir del campo. «Elige al
  // menos una sucursal» salía como banner rojo DESPUÉS de pulsar; el
  // teléfono mal escrito, como un 422 que no dice cuál de los campos era.
  const reglas = {
    nombre_completo: (v) => (v.trim().length < 3 ? 'Escribe el nombre completo.' : null),
    telefono: (v) => (/^\+?\d{8,15}$/.test(v.replace(/[\s()-]/g, ''))
      ? null : 'Teléfono inválido: de 8 a 15 dígitos.'),
    // La misma forma que impone la base (`usuarios_nombre_usuario_forma`),
    // dicha aquí para que el mensaje sea humano en vez de un 422.
    nombre_usuario: (v) => (/^[a-z0-9][a-z0-9._-]{2,31}$/.test(v.trim().toLowerCase())
      ? null
      : 'De 3 a 32 caracteres: letras, números, punto, guion o guion bajo. '
        + 'Por ejemplo: jlopez, ana.ruiz, turno-2.'),
  };

  // SE PROPONE UN USUARIO A PARTIR DEL NOMBRE, y se puede cambiar. Nadie
  // sabe qué escribir en «usuario» la primera vez, y dejar el campo en
  // blanco es lo que hace que cien altas seguidas acaben en usuario1,
  // usuario2, usuario3.
  function sugerirUsuario(nombre) {
    const partes = nombre.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase().split(/\s+/).filter(Boolean);
    if (partes.length < 2) return partes[0]?.replace(/[^a-z0-9]/g, '') || '';
    return `${partes[0][0]}${partes[1]}`.replace(/[^a-z0-9]/g, '');
  }

  function toggleSuc(id) {
    setF((s) => ({
      ...s,
      sucursales_ids: s.sucursales_ids.includes(id)
        ? s.sucursales_ids.filter((x) => x !== id)
        : [...s.sucursales_ids, id],
    }));
  }

  async function enviar(e) {
    e.preventDefault();
    setError(null);
    setTocadoSucursales(true);
    if (f.sucursales_ids.length === 0) return;   // lo dice el propio grupo
    setEnviando(true);
    try {
      // SE MANDA SOLO LO QUE ESTA FORMA USA. Mandar un teléfono vacío en
      // el alta con usuario haría que el servidor lo rechazara por
      // formato, y el mensaje hablaría de un campo que la persona ni vio.
      const cuerpo = {
        nombre_completo: f.nombre_completo,
        rol: f.rol,
        sucursales_ids: f.sucursales_ids,
      };
      if (pideUsuario) cuerpo.nombre_usuario = f.nombre_usuario.trim().toLowerCase();
      else cuerpo.telefono = f.telefono;

      // La persona viaja con la respuesta: la hoja de bienvenida y el correo
      // (carril accesos) la necesitan y en la base solo viven las huellas.
      const persona = { nombre_completo: f.nombre_completo, nombre_usuario: cuerpo.nombre_usuario || null, telefono: cuerpo.telefono || null };
      onCreada(esDirecta
        ? { ...await api.post('/usuarios/alta-directa', cuerpo), forma: 'directa' }
        : { ...await api.post('/usuarios/invitaciones', cuerpo), ...persona, forma });
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo invitar.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal titulo="Dar de alta a alguien" onCerrar={onCerrar}>
      <form onSubmit={enviar} className="space-y-5">
        {/* LAS TRES FORMAS, ARRIBA Y CON TEXTO. Si la de «usuario»
            viviera detrás de un enlace pequeño, quien tiene delante a un
            empleado sin teléfono no la encontraría y acabaría inventando
            un número. */}
        <fieldset className="space-y-2">
          <legend className="text-sm font-semibold text-gray-900">
            ¿Cómo le vas a dar el acceso?
          </legend>
          <div className="grid gap-2 sm:grid-cols-3">
            {FORMAS.map((op) => (
              <button
                key={op.id} type="button"
                aria-pressed={forma === op.id}
                onClick={() => { setForma(op.id); setError(null); }}
                className={`rounded-lg border px-3 py-2.5 text-left transition-colors ${
                  forma === op.id
                    ? 'border-[var(--color-marca)] bg-blue-50'
                    : 'border-gray-200 hover:border-gray-400'}`}
              >
                <span className="block text-sm font-semibold text-gray-900">{op.titulo}</span>
                <span className="block text-xs leading-snug text-gray-500">{op.pie}</span>
              </button>
            ))}
          </div>
          {esDirecta && (
            /* SE DICE EL COSTE ANTES DE ELEGIRLA, no después. */
            <p className="text-xs leading-relaxed text-amber-800 bg-amber-50
                          border border-amber-200 rounded p-2">
              La contraseña temporal la vas a ver tú, así que la conocerás
              hasta que la persona la cambie —TEGO se lo pedirá en cuanto
              entre—. Si puedes esperar, «Con un usuario» es mejor: nadie
              más que ella conoce su clave.
            </p>
          )}
        </fieldset>

        <Ayuda id="usuarios.invitar_sin_correo" />
        <Seccion titulo="Quién entra"
                 descripcion={pideUsuario
                   ? 'El usuario se lo pones tú y no se puede reutilizar después, ni siquiera si la persona se va.'
                   : 'La invitación se manda por WhatsApp, así que el teléfono tiene que estar bien a la primera.'}>
          <CampoValidado etiqueta="Nombre completo" requerido ancho="completo"
                         valor={f.nombre_completo} validar={reglas.nombre_completo}>
            {(props) => (
              <input {...props} value={f.nombre_completo}
                     onChange={(e) => setF((s) => {
                       const nombre = e.target.value;
                       // El usuario se propone mientras se escribe el
                       // nombre, pero SOLO si nadie lo ha tocado: pisar
                       // lo que alguien acaba de escribir es peor que no
                       // proponer nada.
                       const auto = !s.usuario_tocado
                         ? { nombre_usuario: sugerirUsuario(nombre) } : {};
                       return { ...s, nombre_completo: nombre, ...auto };
                     })} />
            )}
          </CampoValidado>
          {pideUsuario ? (
            <CampoValidado
              etiqueta="Usuario" requerido
              valor={f.nombre_usuario} validar={reglas.nombre_usuario}
              ayuda="Con esto entra a TEGO, junto con la clave de tu empresa. Se lo propusimos a partir de su nombre; cámbialo si quieres."
            >
              {(props) => (
                <input {...props} value={f.nombre_usuario} placeholder="jlopez"
                       autoCapitalize="none" autoCorrect="off" spellCheck="false"
                       onChange={(e) => setF((s) => ({
                         ...s,
                         nombre_usuario: e.target.value.toLowerCase(),
                         usuario_tocado: true,
                       }))} />
              )}
            </CampoValidado>
          ) : (
            <CampoValidado etiqueta="Teléfono (WhatsApp)" requerido
                           valor={f.telefono} validar={reglas.telefono}>
              {(props) => (
                <input {...props} value={f.telefono} inputMode="tel" placeholder="5512345678"
                       onChange={(e) => setF((s) => ({ ...s, telefono: e.target.value }))} />
              )}
            </CampoValidado>
          )}
          <CampoValidado etiqueta="Rol" valor={f.rol}
                         ayuda="Se puede cambiar después sin volver a invitar.">
            {(props) => (
              <select {...props} value={f.rol}
                      onChange={(e) => setF((s) => ({ ...s, rol: e.target.value }))}>
                {ROLES_INVITABLES.map((r) => <option key={r} value={r}>{etiquetaRol(r)}</option>)}
              </select>
            )}
          </CampoValidado>
        </Seccion>

        {/* `fieldset`/`legend` y no una etiqueta suelta: son ocho casillas
            que responden a UNA pregunta, y sin agruparlas un lector de
            pantalla las lee como ocho preguntas sueltas. */}
        <fieldset className="space-y-2">
          <legend className="text-sm font-semibold text-gray-900">Sucursales con acceso</legend>
          <p className="text-xs text-gray-500">
            Solo verá el inventario de las que marques. Al menos una.
          </p>
          <div className="space-y-1 border border-gray-200 rounded-lg p-2 max-h-40 overflow-y-auto">
            {sucursales.length === 0 && <div className="text-xs text-gray-500">No hay sucursales.</div>}
            {sucursales.map((s) => (
              <label key={s.id} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={f.sucursales_ids.includes(s.id)} onChange={() => toggleSuc(s.id)} />
                {s.nombre}
              </label>
            ))}
          </div>
          {tocadoSucursales && f.sucursales_ids.length === 0 && (
            <span role="alert" className="block text-xs text-red-700">Elige al menos una sucursal.</span>
          )}
        </fieldset>

        {error && <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2.5">{error}</div>}
        <button type="submit" disabled={enviando} className="w-full bg-[var(--color-marca)] hover:bg-[var(--color-marca-fuerte)] disabled:opacity-60 text-white font-semibold py-2.5 rounded-lg text-sm inline-flex items-center justify-center gap-2">
          <Send className="h-4 w-4" />
          {enviando ? 'Generando…'
            : esDirecta ? 'Crear cuenta y ver la contraseña' : 'Generar invitación'}
        </button>
      </form>
    </Modal>
  );
}

// LO QUE SE ENSEÑA UNA SOLA VEZ, Y LO DICE.
//
// Tres formas, tres cosas distintas que entregar, y la pantalla no las
// mezcla: un código para teclear no se explica igual que una contraseña
// temporal. Lo único que comparten es el aviso de que no se vuelve a
// ver — y ese aviso va ARRIBA, antes de que alguien cierre la ventana.
function ModalCodigo({ datos, onCerrar }) {
  if (datos.forma === 'directa') return <ModalAltaDirecta datos={datos} onCerrar={onCerrar} />;

  const enlace = `${window.location.origin}${datos.enlace}`;
  const porUsuario = datos.forma === 'usuario';
  return (
    <Modal titulo="Invitación lista" onCerrar={onCerrar}>
      <div className="space-y-3 text-center">
        <p className="text-sm font-semibold text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
          Esta es la única vez que se ve el código. No se guarda en ningún
          sitio: si se pierde, se retira esta invitación y se hace otra.
        </p>
        <p className="text-sm text-gray-600">
          {porUsuario
            ? 'Imprime esta hoja o apunta el código: la persona lo teclea y elige su contraseña.'
            : 'Comparte esto con el empleado por WhatsApp.'}
        </p>
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
          <div className="text-xs text-gray-500 uppercase">Código</div>
          <div className="text-3xl font-bold tracking-widest text-gray-900">{datos.codigo}</div>
        </div>
        <BotonCopiar
          texto={mensajeDeInvitacion(enlace, datos.codigo)}
          etiqueta="Copiar mensaje"
        />
        {/* AC-2 · el MISMO mensaje, abierto en el WhatsApp de quien pulsa.
            TEGO no lo manda: solo abre la aplicación con el texto escrito. */}
        <a
          href={enlaceWhatsapp(mensajeDeInvitacion(enlace, datos.codigo), datos.telefono)}
          target="_blank" rel="noreferrer"
          className="w-full inline-flex items-center justify-center gap-2 border border-green-600 text-green-800 bg-green-50 hover:bg-green-100 font-semibold py-2.5 rounded-lg text-sm"
        >
          <Send className="h-4 w-4" aria-hidden="true" /> Compartir por WhatsApp
        </a>
        <p className="text-xs text-gray-500">
          Abre WhatsApp con el mensaje ya escrito{datos.telefono ? ' para ese teléfono' : ''}; tú decides si lo envías.
        </p>
        <div className="text-xs text-gray-500 break-all">{enlace}</div>
        <EntregaDeInvitacion datos={datos} />
      </div>
    </Modal>
  );
}

// =====================================================================
// RESTABLECER LA CONTRASEÑA DE ALGUIEN · CREDENCIALES A2
// =====================================================================
// Tres pasos en un solo diálogo: elegir el canal entre los que EXISTEN de
// verdad para esa persona (los que no, apagados y con el porqué), escribir
// el motivo, confirmar. El resultado dice el estado real: por pantalla se
// ve la temporal una sola vez y hasta cuándo sirve; por Telegram o correo
// se ve «pendiente» hasta que el canal marque la entrega.
const ETIQUETA_CANAL = { TELEGRAM: 'Por Telegram', CORREO: 'Por correo', PANTALLA: 'En pantalla' };

function fechaCorta(iso) {
  try {
    return new Date(iso).toLocaleString('es-MX', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function ModalRestablecerContrasena({ empleado: u, onCerrar, onAplicado }) {
  const [canales, setCanales] = useState(null);
  const [info, setInfo] = useState(null);
  const [errorCarga, setErrorCarga] = useState(null);
  const [canal, setCanal] = useState('');
  const [motivo, setMotivo] = useState('');
  const [codigo, setCodigo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    let vivo = true;
    api.get(`/usuarios/${u.id}/credenciales/canales`)
      .then((d) => {
        if (!vivo) return;
        setCanales(d.canales);
        setInfo(d);
        const primero = d.canales.find((c) => c.disponible);
        if (primero) setCanal(primero.canal);
      })
      .catch((e) => { if (vivo) setErrorCarga(e.message || 'No se pudieron consultar los canales. Revisa la conexión y vuelve a abrir.'); });
    return () => { vivo = false; };
  }, [u.id]);

  const pideCodigo = Boolean(info?.pide_segundo_factor);
  const valido = Boolean(canal) && motivo.trim().length >= 4 && (!pideCodigo || codigo.trim().length >= 6);

  const enviar = async () => {
    setEnviando(true);
    setError(null);
    try {
      const d = await api.post(`/usuarios/${u.id}/restablecer`, {
        canal, motivo: motivo.trim(), ...(pideCodigo ? { codigo_segundo_factor: codigo.trim() } : {}),
      });
      setResultado(d);
    } catch (e) {
      setError(e.message || 'No se pudo restablecer. Revisa los datos e inténtalo otra vez.');
    } finally {
      setEnviando(false);
    }
  };

  if (resultado) {
    return (
      // Al cerrar el resultado se recarga la lista: `onAplicado` cierra el
      // panel de ajustes, y hacerlo antes taparía la temporal.
      <Modal titulo={`Contraseña restablecida · ${u.nombre_completo}`} onCerrar={() => onAplicado()}>
        <div className="space-y-3 text-center">
          {resultado.contrasena_temporal ? (
            <>
              <p className="text-sm font-semibold text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                {resultado.aviso}
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-2">
                <div>
                  <div className="text-xs text-gray-500 uppercase">Usuario</div>
                  <div className="text-xl font-bold text-gray-900">{u.nombre_usuario || u.email}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase">Contraseña temporal</div>
                  <div className="font-mono text-xl font-bold tracking-wide text-gray-900 select-all">
                    {resultado.contrasena_temporal}
                  </div>
                </div>
                <div className="text-xs text-gray-600">Sirve hasta el {fechaCorta(resultado.caduca_en)}.</div>
              </div>
              <BotonCopiar
                texto={`Usuario: ${u.nombre_usuario || u.email} — Contraseña: ${resultado.contrasena_temporal}`}
                etiqueta="Copiar usuario y contraseña"
              />
            </>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-1 text-left">
              <div className="text-sm font-semibold text-gray-900">
                {ETIQUETA_CANAL[resultado.canal]} · {resultado.destino || 'su destino'}
              </div>
              <div className="text-sm text-gray-700">
                Estado: <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800">Pendiente de entrega</span>
              </div>
              <div className="text-xs text-gray-600">Caduca el {fechaCorta(resultado.caduca_en)}. En su ficha verás cuando llegue.</div>
            </div>
          )}
          <p className="text-xs leading-relaxed text-gray-500">{resultado.mensaje}</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      titulo={`Restablecer la contraseña de ${u.nombre_completo}`}
      subtitulo="La contraseña anterior deja de servir en este instante y sus sesiones abiertas se cierran."
      onCerrar={onCerrar}
      pie={(
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onCerrar} className="px-3 py-2 text-sm rounded-lg border border-gray-200">Cancelar</button>
          <button
            type="button"
            onClick={enviar}
            disabled={!valido || enviando || !canales}
            title={!valido ? 'Elige un canal disponible y escribe el motivo (mínimo 4 letras).' : undefined}
            className="px-3 py-2 text-sm rounded-lg bg-[var(--color-marca)] text-white font-semibold disabled:opacity-50"
          >
            {enviando ? 'Restableciendo…' : 'Restablecer contraseña'}
          </button>
        </div>
      )}
    >
      <div className="space-y-3">
        {errorCarga && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5">{errorCarga}</p>}
        {!canales && !errorCarga && <p className="text-sm text-gray-500">Consultando por dónde se le puede entregar…</p>}
        {canales && (
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-gray-800">¿Por dónde se le entrega?</legend>
            {canales.map((c) => (
              <label
                key={c.canal}
                className={`flex items-start gap-2 border rounded-lg p-2.5 ${c.disponible ? 'border-gray-200 cursor-pointer' : 'border-gray-100 opacity-60'}`}
              >
                <input
                  type="radio" name="canal" value={c.canal} className="mt-1"
                  disabled={!c.disponible} checked={canal === c.canal}
                  onChange={() => setCanal(c.canal)}
                />
                <span>
                  <span className="text-sm font-semibold text-gray-900">{ETIQUETA_CANAL[c.canal] || c.canal}</span>
                  {c.destino && <span className="text-sm text-gray-600"> · {c.destino}</span>}
                  <span className="block text-xs text-gray-500">{c.disponible ? 'Disponible ahora.' : c.porque}</span>
                </span>
              </label>
            ))}
          </fieldset>
        )}
        <CampoValidado
          etiqueta="Motivo" requerido valor={motivo}
          ayuda="Queda escrito en la bitácora junto a quién lo hizo y cuándo."
          validar={(v) => (v.trim().length >= 4 ? null : 'Escribe por qué se restablece (mínimo 4 letras).')}
        >
          {(props) => (
            <input {...props} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Perdió el teléfono con la contraseña" />
          )}
        </CampoValidado>
        {pideCodigo && (
          info?.tiene_segundo_factor ? (
            <CampoValidado
              etiqueta="Código de tu segundo factor" requerido valor={codigo}
              ayuda="Es tu propia cuenta: se pide el código de seis dígitos de tu aplicación, o uno de respaldo."
              validar={(v) => (v.trim().length >= 6 ? null : 'Escribe el código de seis dígitos.')}
            >
              {(props) => (
                <input {...props} inputMode="numeric" autoComplete="one-time-code" value={codigo} onChange={(e) => setCodigo(e.target.value)} />
              )}
            </CampoValidado>
          ) : (
            <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
              Es tu propia cuenta y no tienes el segundo factor activado, así que desde aquí no se puede.
              Actívalo en Mi cuenta → Seguridad, o usa la recuperación de Mi cuenta.
            </p>
          )
        )}
        {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5">{error}</p>}
      </div>
    </Modal>
  );
}

// =====================================================================
// LAS CREDENCIALES ENTREGADAS, A LA VISTA · CREDENCIALES A3
// =====================================================================
// Una tabla con los cuatro estados (cargando, error, vacía, llena), el
// estado derivado de cada entrega, quién la emitió, por dónde y a qué
// destino enmascarado. «Reenviar» es emitir de nuevo (la anterior queda
// revocada) y abre el mismo diálogo que «Restablecer contraseña»;
// «Revocar» pide motivo.
const ETIQUETA_ESTADO_ENTREGA = {
  PENDIENTE: { etiqueta: 'Pendiente', clase: 'bg-amber-100 text-amber-800' },
  ENTREGADA: { etiqueta: 'Entregada', clase: 'bg-blue-100 text-blue-800' },
  RECLAMADA: { etiqueta: 'Usada', clase: 'bg-green-100 text-green-800' },
  EXPIRADA: { etiqueta: 'Caducada', clase: 'bg-red-100 text-red-800' },
  REVOCADA: { etiqueta: 'Revocada', clase: 'bg-gray-200 text-gray-700' },
};

function ModalCredenciales({ empleado: u, onCerrar, onAplicado }) {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [reenviar, setReenviar] = useState(false);
  const [revocar, setRevocar] = useState(null);
  const [desvincular, setDesvincular] = useState(false);
  const [cambio, setCambio] = useState(0);
  // CREDENCIALES B5 (carril integraciones, 14 sep 2026): el QR para que la
  // persona vincule su Telegram; al abrirlo el bot le entrega una temporal.
  const [qr, setQr] = useState(null);
  const [errQr, setErrQr] = useState(null);
  async function pedirQr() {
    setErrQr(null);
    try { setQr(await api.post(`/telegram/vinculo/${u.id}`, { motivo: 'vincular Telegram' })); }
    catch (e) { setErrQr(e.message || 'No se pudo armar el enlace.'); }
  }

  useEffect(() => {
    let vivo = true;
    api.get(`/usuarios/${u.id}/credenciales`)
      .then((d) => { if (vivo) { setDatos(d); setError(null); } })
      .catch((e) => { if (vivo) setError(e.message || 'No se pudieron consultar las credenciales. Revisa la conexión y vuelve a abrir.'); });
    return () => { vivo = false; };
  }, [u.id, cambio]);

  if (reenviar) {
    return <ModalRestablecerContrasena empleado={u} onCerrar={() => setReenviar(false)} onAplicado={onAplicado} />;
  }
  if (revocar) {
    return (
      <ModalConfirmar
        titulo="Revocar esta credencial"
        descripcion="La contraseña temporal deja de abrir en este instante. Si la persona la necesita, se le emite otra."
        pedirMotivo
        peligro
        etiquetaConfirmar="Revocar"
        onConfirmar={async (motivo) => {
          await api.post(`/usuarios/${u.id}/credenciales/${revocar.id}/revocar`, { motivo });
          setRevocar(null);
          setCambio((n) => n + 1);
        }}
        onCerrar={() => setRevocar(null)}
      />
    );
  }
  if (desvincular) {
    return (
      <ModalConfirmar
        titulo={`Desvincular el Telegram de ${u.nombre_completo}`}
        descripcion="Deja de servir para recuperar su contraseña y para recibir credenciales. Para volver a usarlo, la persona se vincula otra vez desde el bot."
        pedirMotivo
        peligro
        etiquetaConfirmar="Desvincular"
        onConfirmar={async (motivo) => {
          await api.post(`/usuarios/${u.id}/telegram/desvincular`, { motivo });
          setDesvincular(false);
          setCambio((n) => n + 1);
        }}
        onCerrar={() => setDesvincular(false)}
      />
    );
  }

  const vivas = (datos?.entregas || []).filter((e) => ['PENDIENTE', 'ENTREGADA', 'EXPIRADA'].includes(e.estado));

  return (
    <Modal
      titulo={`Credenciales de ${u.nombre_completo}`}
      subtitulo="Qué se le ha entregado, por dónde y en qué quedó. Nunca se vuelve a ver una contraseña ya entregada."
      onCerrar={onCerrar}
      pie={(
        <div className="flex flex-wrap gap-2 justify-end">
          {datos?.telegram && (
            <button type="button" onClick={() => setDesvincular(true)} className="px-3 py-2 text-sm rounded-lg border border-red-200 text-red-700">
              Desvincular Telegram
            </button>
          )}
          {datos && !datos.telegram && (
            <button type="button" onClick={pedirQr} className="px-3 py-2 text-sm rounded-lg border border-gray-200">
              Vincular Telegram (QR)
            </button>
          )}
          <button type="button" onClick={() => setReenviar(true)} className="px-3 py-2 text-sm rounded-lg bg-[var(--color-marca)] text-white font-semibold">
            {vivas.length ? 'Reenviar (emite otra)' : 'Emitir credencial'}
          </button>
        </div>
      )}
    >
      <div className="space-y-3">
        {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5">{error}</p>}
        {!datos && !error && <Cargando filas={4} />}
        {datos && (
          <div className="text-sm text-gray-700">
            <span className="font-medium">Telegram:</span>{' '}
            {datos.telegram
              ? <>vinculado desde el {fechaCorta(datos.telegram.vinculado_en)}{datos.telegram.telefono ? ` · ${datos.telegram.telefono}` : ''}</>
              : 'sin vincular. La persona puede vincularlo escribiéndole al bot.'}
          </div>
        )}
        {errQr && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5">{errQr}</p>}
        {qr && (
          <div className="text-center bg-gray-50 border border-gray-200 rounded-xl p-3 space-y-2">
            <img src={`data:image/png;base64,${qr.qr_png_base64}`} alt="QR para vincular Telegram" className="mx-auto h-44 w-44" />
            <p className="text-xs text-gray-700">{qr.mensaje}</p>
            <p className="text-xs text-gray-500 break-all">{qr.enlace}</p>
            <p className="text-xs font-semibold text-amber-900">{qr.aviso}</p>
          </div>
        )}
        {datos && datos.entregas.length === 0 && (
          <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
            Sin credenciales temporales entregadas. Cuando se le restablezca la contraseña, aparecerá aquí con su estado.
          </div>
        )}
        {datos && datos.entregas.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 uppercase">
                  <th className="py-1 pr-2">Cuándo</th>
                  <th className="py-1 pr-2">Qué</th>
                  <th className="py-1 pr-2">Por dónde</th>
                  <th className="py-1 pr-2">Estado</th>
                  <th className="py-1 pr-2">Quién</th>
                  <th className="py-1"></th>
                </tr>
              </thead>
              <tbody>
                {datos.entregas.map((e) => {
                  const est = ETIQUETA_ESTADO_ENTREGA[e.estado] || { etiqueta: e.estado, clase: 'bg-gray-200 text-gray-700' };
                  const viva = ['PENDIENTE', 'ENTREGADA', 'EXPIRADA'].includes(e.estado);
                  return (
                    <tr key={e.id} className="border-t border-gray-100 align-top">
                      <td className="py-1.5 pr-2 whitespace-nowrap">{fechaCorta(e.emitida_en)}<div className="text-xs text-gray-500">caduca {fechaCorta(e.expira_en)}</div></td>
                      <td className="py-1.5 pr-2">{e.tipo === 'ALTA' ? 'Alta' : 'Restablecimiento'}</td>
                      <td className="py-1.5 pr-2">{ETIQUETA_CANAL[e.canal] || e.canal}{e.destino_enmascarado ? <div className="text-xs text-gray-500">{e.destino_enmascarado}</div> : null}</td>
                      <td className="py-1.5 pr-2">
                        <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded ${est.clase}`}>{est.etiqueta}</span>
                        {e.entregada_detalle && <div className="text-xs text-gray-500">{e.entregada_detalle}</div>}
                      </td>
                      <td className="py-1.5 pr-2">{e.emitida_por_nombre || '—'}{e.motivo ? <div className="text-xs text-gray-500">{e.motivo}</div> : null}</td>
                      <td className="py-1.5 text-right">
                        {viva && (
                          <button type="button" onClick={() => setRevocar(e)} className="text-xs font-semibold text-red-700">Revocar</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}

// =====================================================================
// EL PUESTO, COMO CATÁLOGO · SUPREMO A4
// =====================================================================
function ModalPuesto({ empleado: u, onCerrar, onAplicado }) {
  const [puestos, setPuestos] = useState(null);
  const [error, setError] = useState(null);
  const [elegido, setElegido] = useState('');
  const [nuevo, setNuevo] = useState({ clave: '', nombre: '', area: '' });
  const [creando, setCreando] = useState(false);

  useEffect(() => {
    let vivo = true;
    api.get('/supremo/puestos')
      .then((d) => { if (vivo) setPuestos(d.puestos); })
      .catch((e) => { if (vivo) setError(e.message || 'No se pudieron cargar los puestos.'); });
    return () => { vivo = false; };
  }, []);

  async function crear() {
    setError(null);
    try {
      const p = await api.post('/supremo/puestos', { clave: nuevo.clave.trim(), nombre: nuevo.nombre.trim(), area: nuevo.area.trim() || null });
      setPuestos((ps) => [...(ps || []), p]);
      setElegido(String(p.id));
      setCreando(false);
      setNuevo({ clave: '', nombre: '', area: '' });
    } catch (e) { setError(e.message); }
  }

  return (
    <ModalConfirmar
      titulo={`Cambiar el puesto de ${u.nombre_completo}`}
      descripcion="Queda con fecha y quién lo cambió. Dejarlo vacío lo quita del puesto actual."
      extra={(
        <div className="space-y-2">
          {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5">{error}</p>}
          {!puestos && !error && <p className="text-sm text-gray-500">Cargando puestos…</p>}
          {puestos && puestos.length === 0 && !creando && (
            <p className="text-sm text-gray-600">Tu empresa no tiene puestos. Crea el primero aquí abajo.</p>
          )}
          {puestos && puestos.length > 0 && (
            <CampoValidado etiqueta="Puesto" valor={elegido} ayuda="Los puestos son de tu empresa; se crean una vez y se reutilizan.">
              {(props) => (
                <select {...props} value={elegido} onChange={(e) => setElegido(e.target.value)}>
                  <option value="">Sin puesto</option>
                  {puestos.map((p) => <option key={p.id} value={p.id}>{p.clave} · {p.nombre}{p.area ? ` (${p.area})` : ''}</option>)}
                </select>
              )}
            </CampoValidado>
          )}
          {creando ? (
            <div className="border border-gray-200 rounded-lg p-2 space-y-2">
              <CampoValidado etiqueta="Clave" requerido valor={nuevo.clave} validar={(v) => (v.trim() ? null : 'Una clave corta, por ejemplo ALM.')}>
                {(props) => <input {...props} value={nuevo.clave} onChange={(e) => setNuevo({ ...nuevo, clave: e.target.value.toUpperCase() })} placeholder="ALM" />}
              </CampoValidado>
              <CampoValidado etiqueta="Nombre" requerido valor={nuevo.nombre} validar={(v) => (v.trim().length >= 2 ? null : 'El nombre del puesto.')}>
                {(props) => <input {...props} value={nuevo.nombre} onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })} placeholder="Almacenista" />}
              </CampoValidado>
              <CampoValidado etiqueta="Área" valor={nuevo.area}>
                {(props) => <input {...props} value={nuevo.area} onChange={(e) => setNuevo({ ...nuevo, area: e.target.value })} placeholder="Bodega" />}
              </CampoValidado>
              <div className="flex gap-2">
                <button type="button" onClick={crear} disabled={!nuevo.clave.trim() || nuevo.nombre.trim().length < 2} className="px-3 py-1.5 text-sm rounded-lg bg-[var(--color-marca)] text-white font-semibold disabled:opacity-50">Crear puesto</button>
                <button type="button" onClick={() => setCreando(false)} className="px-3 py-1.5 text-sm rounded-lg border border-gray-200">Cancelar</button>
              </div>
            </div>
          ) : (
            <button type="button" onClick={() => setCreando(true)} className="text-sm font-semibold text-[var(--color-marca)]">+ Crear un puesto nuevo</button>
          )}
        </div>
      )}
      pedirMotivo
      etiquetaConfirmar="Guardar puesto"
      onConfirmar={async (motivo) => {
        await api.post('/supremo/puestos/asignar', { id_usuario: u.id, id_puesto: elegido ? Number(elegido) : null, motivo });
        onAplicado();
      }}
      onCerrar={onCerrar}
    />
  );
}

// =====================================================================
// TRASPASAR EL ROL DE ADMIN SUPREMO · SUPREMO A2
// =====================================================================
function ModalTraspaso({ empleado: u, onCerrar, onAplicado }) {
  const [contrasena, setContrasena] = useState('');
  return (
    <ModalConfirmar
      titulo={`Traspasar el rol de Admin Supremo a ${u.nombre_completo}`}
      descripcion="Es un acto explícito: esa persona podrá todo dentro de la empresa y tú quedarás como administrador. Nadie podrá administrarla, ni tú. Queda en la bitácora con motivo."
      extra={(
        <CampoValidado etiqueta="Tu contraseña" requerido valor={contrasena} ayuda="Una sesión abierta no traspasa sola: hace falta la tuya.">
          {(props) => <CampoContrasena {...props} autoComplete="current-password" value={contrasena} onChange={(e) => setContrasena(e.target.value)} />}
        </CampoValidado>
      )}
      pedirMotivo
      peligro
      palabraConfirmacion="TRASPASAR"
      etiquetaConfirmar="Traspasar el rol"
      onConfirmar={async (motivo) => {
        if (!contrasena) throw new ErrorApi('Escribe tu contraseña para confirmar.');
        await api.post('/supremo/traspasar', { a_usuario: u.id, contrasena, motivo });
        onAplicado();
      }}
      onCerrar={onCerrar}
    />
  );
}

function ModalAltaDirecta({ datos, onCerrar }) {
  return (
    <Modal titulo="Cuenta creada" onCerrar={onCerrar}>
      <div className="space-y-3 text-center">
        <p className="text-sm font-semibold text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
          Esta es la única vez que se ve esta contraseña. Anótala o cópiala
          antes de cerrar; si se pierde, se le repone otra desde su ficha.
        </p>
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-2">
          {/* LA CLAVE DE LA EMPRESA VA PRIMERO (19 sep 2026): es lo primero
              que pide «Con mi usuario» y lo único de las tres cosas que el
              empleado no puede adivinar en su propio teléfono. Medido en
              producción: sin ella, la tarjeta dejaba a la persona ante
              «Clave de tu empresa» sin saber qué poner. */}
          {datos.clave_acceso && (
            <div>
              <div className="text-xs text-gray-500 uppercase">Clave de la empresa</div>
              <div className="font-mono text-xl font-bold tracking-wide text-gray-900 select-all">{datos.clave_acceso}</div>
            </div>
          )}
          <div>
            <div className="text-xs text-gray-500 uppercase">Usuario</div>
            <div className="text-xl font-bold text-gray-900">{datos.nombre_usuario}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase">Contraseña temporal</div>
            {/* Monoespaciada Y con `select-all`: se lee en voz alta o se
                copia de un papel, y hay que poder distinguir sin dudar. */}
            <div className="font-mono text-xl font-bold tracking-wide text-gray-900 select-all">
              {datos.contrasena_temporal}
            </div>
          </div>
          {datos.caduca_en && (
            <div className="text-xs text-gray-600">Sirve hasta el {fechaCorta(datos.caduca_en)}; después hay que emitirle otra.</div>
          )}
        </div>
        <BotonCopiar
          texto={[
            datos.entrar_en ? `Entra en ${datos.entrar_en} → «Con mi usuario»` : null,
            datos.clave_acceso ? `Clave de la empresa: ${datos.clave_acceso}` : null,
            `Usuario: ${datos.nombre_usuario}`,
            `Contraseña temporal: ${datos.contrasena_temporal}`,
          ].filter(Boolean).join('\n')}
          etiqueta={datos.clave_acceso ? 'Copiar todo para mandárselo' : 'Copiar usuario y contraseña'}
        />
        <p className="text-xs leading-relaxed text-gray-500">
          {datos.entrar_en
            ? <>Entra en <b>{datos.entrar_en}</b>, pestaña «Con mi usuario», con estas tres cosas. </>
            : null}
          En cuanto entre, TEGO le va a pedir que la cambie por una suya.
          Hasta entonces no puede hacer nada más.
        </p>
      </div>
    </Modal>
  );
}

function BotonCopiar({ texto, etiqueta }) {
  const [copiado, setCopiado] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard?.writeText(texto); setCopiado(true); setTimeout(() => setCopiado(false), 1500); }}
      className="w-full inline-flex items-center justify-center gap-2 bg-[var(--color-marca)] text-white font-semibold py-2.5 rounded-lg text-sm"
    >
      <Copy className="h-4 w-4" /> {copiado ? 'Copiado' : etiqueta}
    </button>
  );
}


// =====================================================================
// EL GAFETE · T-5, sobre el Anexo B (B-6)
// =====================================================================
// Un cartón con un QR que la persona presenta al lector de una terminal
// cuando no puede traer teléfono al piso.
//
// TRES COSAS QUE ESTA PANTALLA TIENE QUE DECIR, y las tres se descubren
// tarde si no están:
//
//   1. Que **funciona sin red**. Es la mitad del valor del gafete y no
//      se deduce mirando un QR.
//   2. Que **reimprimir no invalida el cartón viejo**. Quien no lo sepa
//      va a reimprimir «por si acaso» pensando que anula el perdido, y
//      dejará circulando una credencial que cree cancelada.
//   3. Que **cancelar sí lo mata**, y que la terminal se entera en su
//      siguiente latido — no instantáneamente en una nave sin red.
//
// EL CARTÓN LLEVA EL NOMBRE Y EL QR NO. Dentro del código solo van
// números (empresa, persona, versión, vencimiento, llave): un gafete se
// pierde en un estacionamiento y lo que se pierde con él no puede ser un
// directorio de personal. Pero el cartón lo mira una persona en la
// entrada de la planta, así que el nombre va impreso al lado, en tinta.
export function ModalGafete({ empleado: u, onCerrar }) {
  const [datos, setDatos] = useState(null);
  const [token, setToken] = useState(null);
  const [mensaje, setMensaje] = useState('');
  const [error, setError] = useState('');
  const [trabajando, setTrabajando] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [motivo, setMotivo] = useState('');

  useEffect(() => {
    api.get('/gafetes')
      .then((lista) => setDatos(lista.find((g) => g.id_usuario === u.id && g.activo) || false))
      .catch(() => setDatos(false));
  }, [u.id]);

  const emitir = async () => {
    setTrabajando(true); setError('');
    try {
      const r = await api.post(`/gafetes/${u.id}`, {});
      setToken(r.token);
      setDatos(r);
      setMensaje(r.mensaje || '');
    } catch (e) {
      setError(e instanceof ErrorApi ? e.mensaje : 'No se pudo emitir el gafete.');
    } finally {
      setTrabajando(false);
    }
  };

  const revocar = async () => {
    if (motivo.trim().length < 3) {
      setError('Escribe por qué lo cancelas. Dentro de seis meses «cancelado» a secas no dice si la persona se fue o si el cartón anda perdido.');
      return;
    }
    setTrabajando(true); setError('');
    try {
      const r = await api.post(`/gafetes/${u.id}/revocar`, { motivo });
      setDatos(false); setToken(null); setCancelando(false);
      setMensaje(r.mensaje || '');
    } catch (e) {
      setError(e instanceof ErrorApi ? e.mensaje : 'No se pudo cancelar el gafete.');
    } finally {
      setTrabajando(false);
    }
  };

  return (
    <Modal titulo={`Gafete de ${u.nombre_completo}`} icono={IdCard} onCerrar={onCerrar} bloqueado={trabajando}>
      <div className="space-y-3">
        <NotaExplicativa>
          Es un cartón con un código que se presenta al lector de la terminal,
          para quien no puede traer teléfono al piso.{' '}
          <strong>Funciona aunque se caiga la red</strong>: la terminal lo
          comprueba sola.
        </NotaExplicativa>

        {datos === null && <p className="text-sm text-gray-500">Buscando…</p>}

        {datos === false && !token && (
          <>
            <p className="text-sm text-gray-600">
              {u.nombre_completo} no tiene ningún gafete activo.
            </p>
            <button
              onClick={emitir}
              disabled={trabajando}
              className="w-full inline-flex items-center justify-center gap-2 bg-[var(--color-marca)] text-white font-semibold py-2.5 rounded-lg text-sm disabled:opacity-50"
            >
              <Printer className="h-4 w-4" /> {trabajando ? 'Emitiendo…' : 'Imprimir gafete'}
            </button>
          </>
        )}

        {datos && (
          <>
            {token ? (
              <HojaDeGafete persona={u.nombre_completo} token={token} vence={datos.vence_en} />
            ) : (
              <button
                onClick={emitir}
                disabled={trabajando}
                className="w-full inline-flex items-center justify-center gap-2 border border-gray-300 bg-white text-gray-700 font-semibold py-2.5 rounded-lg text-sm disabled:opacity-50"
              >
                <Printer className="h-4 w-4" /> Volver a imprimirlo
              </button>
            )}

            <dl className="text-sm space-y-1">
              <Dato campo="Vence" valor={String(datos.vence_en || '').slice(0, 10)} />
              <Dato campo="Versión" valor={datos.version} />
              <Dato campo="Veces impreso" valor={datos.impreso_veces} />
            </dl>

            {/* LA FRASE QUE EVITA EL ERROR MÁS CARO DE ESTA PANTALLA. */}
            <p className="text-xs text-gray-500">
              Volver a imprimirlo da <strong>el mismo código</strong>: el cartón
              anterior sigue funcionando. Si se perdió y no quieres que sirva,
              cancélalo.
            </p>

            {!cancelando ? (
              <button
                onClick={() => { setCancelando(true); setError(''); }}
                className="w-full border border-red-200 text-red-700 text-sm font-semibold py-2 rounded-lg hover:bg-red-50"
              >
                Cancelar este gafete
              </button>
            ) : (
              <div className="space-y-2 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">
                  El cartón dejará de abrir terminales. Una terminal sin red se
                  entera en su siguiente latido.
                </p>
                {/* `CampoValidado` pinta el control que le pasan como función;
                    aquí se le pasaba `onChange` y ningún hijo, y la pantalla
                    entera caía al «Algo falló en la aplicación» al pulsar
                    «Cancelar este gafete». Lo midió la auditoría de botones
                    del 19 de septiembre de 2026 (era el único uso así). */}
                <CampoValidado
                  etiqueta="¿Por qué lo cancelas?"
                  ayuda="Se perdió, la persona se fue, se imprimió por error…"
                  requerido
                  valor={motivo}
                  validar={(v) => (v.trim().length >= 3 ? null : 'Escribe el motivo (mínimo 3 letras).')}
                >
                  {(props) => <input {...props} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Se perdió el cartón" />}
                </CampoValidado>
                <div className="flex gap-2">
                  <button
                    onClick={revocar}
                    disabled={trabajando}
                    className="flex-1 bg-red-600 text-white text-sm font-semibold py-2 rounded-lg disabled:opacity-50"
                  >
                    {trabajando ? 'Cancelando…' : 'Sí, cancelarlo'}
                  </button>
                  <button
                    onClick={() => setCancelando(false)}
                    className="flex-1 border border-gray-300 text-gray-700 text-sm font-semibold py-2 rounded-lg"
                  >
                    Mejor no
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {mensaje && <p className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded p-2">{mensaje}</p>}
        {error && <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</p>}
      </div>
    </Modal>
  );
}

// EL CARTÓN, tal como se imprime. Fondo blanco y tinta negra siempre —lo
// impone `CodigoQR`—, y el nombre grande al lado porque el que mira el
// cartón en la entrada es una persona, no un lector.
export function CartonDeGafete({ persona, token, vence }) {
  return (
    <div className="border-2 border-gray-900 rounded-lg p-3 flex items-center gap-3 bg-white">
      <CodigoQR valor={token} tamano={128} etiqueta={`Gafete de ${persona}`} />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-gray-500">TEGO · Gafete de operador</p>
        <p className="font-bold text-gray-900 leading-tight break-words">{persona}</p>
        <p className="text-[11px] text-gray-500 mt-1">Vence {String(vence || '').slice(0, 10)}</p>
      </div>
    </div>
  );
}


// =====================================================================
// CAMBIAR EL PUESTO DE VARIAS PERSONAS A LA VEZ · Accesos Fase 2
// =====================================================================
// TRES COSAS QUE ESTA VENTANA TIENE QUE DECIR, y las tres se descubren
// tarde si no están:
//
//   1. **A cuántas personas y a quiénes.** Un «47 seleccionadas» sin los
//      nombres es un botón que nadie pulsa con confianza.
//   2. **Que hace falta un motivo**, y por qué: queda un asiento POR
//      PERSONA, y dentro de seis meses la pregunta es «¿quién le quitó
//      esto a Juan?».
//   3. **Que el cambio es inmediato.** No hace falta que vuelvan a
//      entrar, y saberlo evita la llamada de «no me funciona».
// L1-6 · LAS ACCIONES PARA VARIAS PERSONAS A LA VEZ.
//
// El cambio de PUESTO es todo-o-nada (por `/usuarios/privilegios`, como
// siempre): cambiar de rango a medias no tiene sentido. Las demás
// —suspender, devolver acceso, dar de baja, cambiar de área— van POR
// PERSONA (`/usuarios/lote/*`): la que no se puede se rechaza con su
// motivo, las demás siguen, y aquí se enseña el resumen de quién sí y
// quién no.
const ACCIONES_LOTE = [
  { id: 'puesto', etiqueta: 'Cambiar el puesto', ruta: '/usuarios/privilegios',
    verbo: 'cambiar de puesto', todoJunto: true },
  { id: 'suspender', etiqueta: 'Suspender el acceso', ruta: '/usuarios/lote/suspender',
    verbo: 'suspender' },
  { id: 'reactivar', etiqueta: 'Devolver el acceso', ruta: '/usuarios/lote/reactivar',
    verbo: 'reactivar' },
  { id: 'desactivar', etiqueta: 'Dar de baja', ruta: '/usuarios/lote/desactivar',
    verbo: 'dar de baja' },
  { id: 'area', etiqueta: 'Cambiar de área', ruta: '/usuarios/lote/area',
    verbo: 'cambiar de área' },
  // AC-10 (1 oct 2026): dar un permiso a varias personas, con fecha de fin
  // opcional. Va por la misma ruta del puesto (`/usuarios/privilegios`,
  // `conceder` + `vigente_hasta`), que ya lo admitía y no tenía pantalla.
  // Solo se ofrece si el catálogo de permisos llegó.
  { id: 'permiso', etiqueta: 'Dar un permiso', ruta: '/usuarios/privilegios',
    verbo: 'dar un permiso', todoJunto: true, pideCatalogo: true },
];

// Se exporta para que la prueba de AC-10 lo monte solo (`pruebas/seg4bAc10.test.js`).
export function ModalBloque({ ids, nombres, areas, onCerrar, onAplicado }) {
  const [accionId, setAccionId] = useState('puesto');
  const [rol, setRol] = useState('');
  const [dias, setDias] = useState(7);
  const [idArea, setIdArea] = useState('');   // '' = sin área
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);  // {aplicados, rechazados}
  // AC-10 · el permiso a dar y su fecha de fin. `hoy` lo da el servidor
  // (ver accesos/vigenciaDePermiso.js); sin él, el campo no se valida
  // aquí y el servidor sigue rechazando una fecha pasada.
  const [catalogo, setCatalogo] = useState([]);
  const [permiso, setPermiso] = useState('');
  const [hasta, setHasta] = useState('');
  const [hoy, setHoy] = useState(null);

  useEffect(() => {
    let vivo = true;
    api.get('/permisos/catalogo').then((c) => { if (vivo) setCatalogo(c || []); }).catch(() => {});
    api.get('/permisos/caducan-pronto?dias=1')
      .then((d) => { if (vivo) setHoy(d?.hoy || null); }).catch(() => {});
    return () => { vivo = false; };
  }, []);

  const acciones = ACCIONES_LOTE.filter((a) => !a.pideCatalogo || catalogo.length > 0);
  const accion = ACCIONES_LOTE.find((a) => a.id === accionId);
  const faltaPuesto = accionId === 'puesto' && !rol;
  const faltaPermiso = accionId === 'permiso' && !permiso;
  const errorHasta = accionId === 'permiso' ? errorDeFechaFin(hasta, hoy) : null;
  const listo = !faltaPuesto && !faltaPermiso && !errorHasta && motivo.trim().length >= 4;
  const porQueGris = faltaPuesto ? 'Elige el puesto.'
    : faltaPermiso ? 'Elige el permiso que vas a dar.'
      : errorHasta ? 'Corrige la fecha de fin, o déjala vacía.'
        : (motivo.trim().length < 4 ? 'Escribe el motivo (mínimo 4 letras).' : null);

  async function enviar(e) {
    e.preventDefault();
    if (!listo || enviando) return;
    setEnviando(true);
    setError(null);
    const cuerpo = { ids, motivo: motivo.trim() };
    if (accionId === 'puesto') cuerpo.rol = rol;
    if (accionId === 'suspender') cuerpo.dias = dias;
    if (accionId === 'area') cuerpo.id_area = idArea === '' ? null : Number(idArea);
    if (accionId === 'permiso') {
      cuerpo.conceder = [permiso];
      cuerpo.vigente_hasta = hasta || null;
    }
    try {
      const r = await api.post(accion.ruta, cuerpo);
      // AC-10: dar un permiso confirma a la vista a quién se le dio y hasta
      // cuándo, en vez de cerrarse sin decir nada.
      if (accionId === 'permiso') {
        const nombrePermiso = catalogo.find((c) => c.codigo === permiso)?.nombre || permiso;
        setResultado({
          mensaje: `${r?.mensaje || 'Listo.'} Permiso: «${nombrePermiso}». ${textoDeVigencia(hasta, hoy, ids.length)}`,
          aplicados: r?.personas || [],
          rechazados: [],
        });
        return;
      }
      // El cambio de puesto no devuelve rechazados (es todo-o-nada); las
      // acciones en lote sí. Si no se rechazó a nadie, se cierra y recarga.
      if (accion.todoJunto || !r?.rechazados?.length) {
        onAplicado();
      } else {
        setResultado(r);
      }
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo aplicar.');
    } finally {
      setEnviando(false);
    }
  }

  // TERMINADO, CON RECHAZOS A LA VISTA. No se cierra solo: quien lanzó el
  // cambio tiene que ver a quién no se le pudo y por qué.
  if (resultado) {
    return (
      <Modal titulo="Resultado del cambio" onCerrar={onAplicado}>
        <div className="space-y-4">
          <p className="text-sm text-gray-800">{resultado.mensaje}</p>
          {resultado.aplicados.length > 0 && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-3">
              <p className="text-xs font-semibold text-green-800 mb-1">
                Se aplicó a {resultado.aplicados.length}
              </p>
              <p className="text-sm text-gray-700">
                {resultado.aplicados.slice(0, 8).map((a) => a.nombre).join(', ')}
                {resultado.aplicados.length > 8 && ` y ${resultado.aplicados.length - 8} más`}
              </p>
            </div>
          )}
          {resultado.rechazados.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-semibold text-amber-800 mb-1">
                No se pudo con {resultado.rechazados.length}
              </p>
              <ul className="text-sm text-gray-700 list-disc pl-5 space-y-0.5">
                {resultado.rechazados.map((x) => {
                  const nombre = nombres[ids.indexOf(x.id)] || `#${x.id}`;
                  return <li key={x.id}><strong>{nombre}</strong>: {x.motivo}</li>;
                })}
              </ul>
            </div>
          )}
          <button
            type="button" onClick={onAplicado}
            className="w-full bg-[var(--color-marca)] hover:bg-[var(--color-marca-fuerte)] text-white font-semibold py-2.5 rounded-lg text-sm"
          >
            Entendido
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal titulo={`Acciones para ${ids.length} persona${ids.length === 1 ? '' : 's'}`}
           onCerrar={onCerrar}>
      <form onSubmit={enviar} className="space-y-4">
        {/* LOS NOMBRES, A LA VISTA. Se enseñan los primeros ocho y se dice
            cuántos faltan: la lista entera de cincuenta no cabe, pero
            «y 42 más» sí deja comprobar que no se coló nadie raro. */}
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="text-xs font-semibold text-gray-700 mb-1">A quiénes</p>
          <p className="text-sm text-gray-800 leading-relaxed">
            {nombres.slice(0, 8).join(', ')}
            {nombres.length > 8 && ` y ${nombres.length - 8} más`}
          </p>
        </div>

        <CampoValidado etiqueta="Qué hacer" requerido valor={accionId}>
          {(props) => (
            <select {...props} value={accionId}
                    onChange={(e) => { setAccionId(e.target.value); setError(null); }}>
              {acciones.map((a) => (
                <option key={a.id} value={a.id}>{a.etiqueta}</option>
              ))}
            </select>
          )}
        </CampoValidado>

        {accionId === 'puesto' && (
          <CampoValidado etiqueta="Puesto nuevo" requerido valor={rol}
                         ayuda="Se le aplica a todas las personas de la lista.">
            {(props) => (
              <select {...props} value={rol} onChange={(e) => setRol(e.target.value)}>
                <option value="">Elige un puesto…</option>
                {ROLES_INVITABLES.map((r) => (
                  <option key={r} value={r}>{etiquetaRol(r)}</option>
                ))}
              </select>
            )}
          </CampoValidado>
        )}

        {accionId === 'suspender' && (
          <CampoValidado etiqueta="Cuántos días" requerido valor={String(dias)}
                         ayuda="El acceso vuelve solo al vencer el plazo; nadie tiene que acordarse.">
            {(props) => (
              <input {...props} type="number" min="1" max="365" value={dias}
                     onChange={(e) => setDias(Number(e.target.value))} />
            )}
          </CampoValidado>
        )}

        {accionId === 'area' && (
          <CampoValidado etiqueta="Área nueva" valor={idArea}
                         ayuda="El área solo agrupa y filtra; no cambia lo que cada quien puede hacer.">
            {(props) => (
              <select {...props} value={idArea} onChange={(e) => setIdArea(e.target.value)}>
                <option value="">Sin área</option>
                {areas.filter((a) => a.activo).map((a) => (
                  <option key={a.id} value={a.id}>{a.nombre}</option>
                ))}
              </select>
            )}
          </CampoValidado>
        )}

        {accionId === 'permiso' && (
          <CampoValidado etiqueta="Qué permiso" requerido valor={permiso}
                         ayuda="Se suma a lo que ya trae su puesto; no le quita nada.">
            {(props) => (
              <select {...props} value={permiso} onChange={(e) => setPermiso(e.target.value)}>
                <option value="">Elige un permiso…</option>
                {catalogo.map((p) => (
                  <option key={p.codigo} value={p.codigo}>{p.nombre}</option>
                ))}
              </select>
            )}
          </CampoValidado>
        )}

        {accionId === 'permiso' && (
          <div>
            <CampoValidado etiqueta="Hasta cuándo (opcional)" valor={hasta}>
              {(props) => (
                <input {...props} type="date" value={hasta} min={hoy || undefined}
                       aria-describedby="bloque-hasta-estado"
                       onChange={(e) => setHasta(e.target.value)} />
              )}
            </CampoValidado>
            <Ayuda id="seguridad.bloque.permiso_hasta" />
            <p id="bloque-hasta-estado" role={errorHasta ? 'alert' : 'status'}
               className={`mt-1 text-xs ${errorHasta ? 'text-red-700' : 'text-gray-700'}`}>
              {errorHasta || textoDeVigencia(hasta, hoy, ids.length)}
            </p>
            {hasta && (
              <button type="button" onClick={() => setHasta('')}
                      className="mt-1 py-1 text-xs font-semibold text-[var(--color-marca)] hover:underline">
                Quitar la fecha (que no caduque)
              </button>
            )}
          </div>
        )}

        <CampoValidado
          etiqueta="Motivo" requerido ancho="completo" valor={motivo}
          ayuda="Queda un asiento por persona con tu nombre y este motivo. Dentro de seis meses, esto es lo que contesta «¿por qué le quitaron el acceso a Juan?»."
          validar={(v) => (v.trim().length < 4 ? 'Escribe por qué haces el cambio.' : null)}
        >
          {(props) => (
            <input {...props} value={motivo}
                   placeholder="Fin de temporada: se les baja el acceso"
                   onChange={(e) => setMotivo(e.target.value)} />
          )}
        </CampoValidado>

        <p className="text-xs leading-relaxed text-gray-500">
          El cambio surte efecto <strong>de inmediato</strong>: no hace
          falta que vuelvan a entrar.{' '}
          {accion?.todoJunto
            ? 'Va todo junto — si alguna persona no se puede cambiar, no se cambia ninguna y TEGO dice cuál.'
            : 'Se aplica persona por persona: la que no se pueda se salta y verás cuál y por qué.'}
        </p>

        {error && (
          <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2.5 whitespace-pre-line">
            {error}
          </div>
        )}
        <button
          type="submit" disabled={!listo || enviando}
          title={porQueGris || undefined}
          aria-describedby={porQueGris ? 'bloque-por-que-gris' : undefined}
          className="w-full bg-[var(--color-marca)] hover:bg-[var(--color-marca-fuerte)] disabled:opacity-60 text-white font-semibold py-2.5 rounded-lg text-sm"
        >
          {enviando ? 'Aplicando…' : `Aplicar a ${ids.length}`}
        </button>
        {/* Por qué está gris, a la vista y no solo al pasar el ratón: en un
            teléfono no hay ratón (BOT-1). */}
        <Motivo id="bloque-por-que-gris" cuando={!enviando && Boolean(porQueGris)}>
          {porQueGris}
        </Motivo>
      </form>
    </Modal>
  );
}

// L1-2 · GESTIONAR EL CATÁLOGO DE ÁREAS. Crear, renombrar, dar de baja y
// reactivar. Baja lógica: dar de baja un área no toca a su gente, solo
// deja de ofrecerla para agrupar y asignar.
export function ModalAreas({ onCerrar, onCambio }) {
  const [areas, setAreas] = useState([]);
  const [nueva, setNueva] = useState('');
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [trabajando, setTrabajando] = useState(false);

  const cargar = useCallback(() => {
    setCargando(true);
    api.get('/usuarios/areas?incluir_inactivas=true')
      .then((a) => setAreas(a))
      .catch((e) => setError(e.message))
      .finally(() => setCargando(false));
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  async function hacer(fn) {
    if (trabajando) return;
    setTrabajando(true);
    setError(null);
    try {
      await fn();
      cargar();
      onCambio?.();
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar.');
    } finally {
      setTrabajando(false);
    }
  }

  const crear = () => hacer(async () => {
    await api.post('/usuarios/areas', { nombre: nueva.trim() });
    setNueva('');
  });
  const renombrar = (a) => {
    const nombre = window.prompt('Nuevo nombre del área', a.nombre);
    if (nombre == null || nombre.trim() === '' || nombre.trim() === a.nombre) return;
    hacer(() => api.put(`/usuarios/areas/${a.id}`, { nombre: nombre.trim() }));
  };
  const cambiarActivo = (a) =>
    hacer(() => api.put(`/usuarios/areas/${a.id}`, { activo: !a.activo }));

  return (
    <Modal titulo="Áreas de la empresa" onCerrar={onCerrar}>
      <div className="space-y-4">
        <NotaExplicativa titulo="¿Para qué sirven las áreas?">
          Un área es una parte de tu empresa —Almacén, Ventas, Mantenimiento—.
          Sirve para <strong>agrupar, filtrar y reportar</strong> a tu gente;
          no cambia lo que cada quien puede hacer. Son tuyas: créalas como las
          llames tú.
        </NotaExplicativa>

        <form onSubmit={(e) => { e.preventDefault(); crear(); }}
              className="flex items-end gap-2">
          <div className="flex-1">
            <label htmlFor="area-nueva" className="block text-xs font-semibold text-gray-700 mb-1">
              Nueva área
            </label>
            <input
              id="area-nueva" value={nueva} maxLength={160}
              onChange={(e) => setNueva(e.target.value)}
              placeholder="Almacén"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <button
            type="submit" disabled={!nueva.trim() || trabajando}
            aria-describedby={!nueva.trim() ? 'motivo-area-nueva' : undefined}
            className="bg-[var(--color-marca)] hover:bg-[var(--color-marca-fuerte)] disabled:opacity-60 text-white font-semibold px-4 py-2 rounded-lg text-sm"
          >
            Crear
          </button>
        </form>
        {/* P12 (29 sep 2026): el censo lo contaba «gris sin decir por qué».
            Apagado con motivo a la vista (Doctrina 2, regla 8). */}
        <Motivo id="motivo-area-nueva" cuando={!nueva.trim()} className="-mt-2">
          Escribe arriba el nombre del área para poder crearla.
        </Motivo>

        {error && (
          <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2.5 whitespace-pre-line">
            {error}
          </div>
        )}

        {cargando ? (
          <Cargando filas={3} />
        ) : areas.length === 0 ? (
          <p className="text-sm text-gray-500">
            Sin áreas. Crea la primera arriba (por ejemplo, «Almacén»).
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
            {areas.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-2 px-3 py-2">
                <div>
                  <span className={`text-sm font-medium ${a.activo ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                    {a.nombre}
                  </span>
                  <span className="ml-2 text-xs text-gray-500">
                    {a.usuarios} persona{a.usuarios === 1 ? '' : 's'}
                    {!a.activo && ' · dada de baja'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {a.activo && (
                    <button type="button" onClick={() => renombrar(a)}
                            disabled={trabajando}
                            className="text-xs font-semibold text-[var(--color-marca)] hover:underline">
                      Renombrar
                    </button>
                  )}
                  <button type="button" onClick={() => cambiarActivo(a)}
                          disabled={trabajando}
                          className="text-xs font-semibold text-gray-600 hover:text-gray-900">
                    {a.activo ? 'Dar de baja' : 'Reactivar'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}


// =====================================================================
// DAR DE ALTA A LA PLANTILLA DESDE UN ARCHIVO · Accesos Fase 2
// =====================================================================
// DOS PASOS Y NO UNO. Primero se enseña qué va a pasar y solo entonces
// se escribe, que es la convención de los importadores de la casa. Aquí
// pesa el doble porque lo que se crea son CREDENCIALES: veinte
// invitaciones mal hechas no se corrigen, se revocan una por una.
function ModalImportarPersonas({ onCerrar, onCreadas }) {
  const [archivo, setArchivo] = useState(null);
  const [revision, setRevision] = useState(null);
  const [error, setError] = useState(null);
  const [trabajando, setTrabajando] = useState(false);

  async function mandar(ruta, alTerminar) {
    if (!archivo) return;
    setTrabajando(true);
    setError(null);
    try {
      // `api.subir` YA EXISTE para esto y manda un solo archivo con el
      // Content-Type que pone el navegador. Añadir una tercera forma de
      // subir sería la cuarta manera de hacer lo mismo en este archivo.
      alTerminar(await api.subir(ruta, archivo));
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo leer el archivo.');
    } finally {
      setTrabajando(false);
    }
  }

  const problemas = revision?.problemas || [];

  return (
    <Modal titulo="Dar de alta desde una lista" onCerrar={onCerrar}>
      <div className="space-y-4">
        <NotaExplicativa titulo="¿Cómo tiene que ser el archivo?">
          Un Excel o un CSV con una columna <strong>Nombre</strong> —lo único
          obligatorio— y, si quieres, <strong>Usuario</strong>,{' '}
          <strong>Puesto</strong> y <strong>Sucursal</strong>. Si no pones
          usuario, TEGO propone uno a partir del nombre y te lo enseña antes
          de crear nada. Baja la plantilla si prefieres partir de ella.
        </NotaExplicativa>

        <a
          href="/api/usuarios/importar/plantilla"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--color-marca)]"
        >
          <FileDown className="h-4 w-4" /> Bajar la plantilla de ejemplo
        </a>

        <div>
          <label htmlFor="archivo-personas" className="block text-sm font-semibold text-gray-900 mb-1">
            Tu archivo
          </label>
          <input
            id="archivo-personas" type="file" accept=".csv,.xlsx,.xlsm"
            onChange={(e) => { setArchivo(e.target.files?.[0] || null); setRevision(null); }}
            className="block w-full text-sm"
          />
        </div>

        {error && (
          <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2.5 whitespace-pre-line">
            {error}
          </div>
        )}

        {revision && (
          <div className="rounded-lg border border-gray-200 p-3 space-y-2">
            <p className="text-sm font-semibold text-gray-900">
              {revision.personas.length} persona(s) por dar de alta
              {problemas.length > 0 && `, y ${problemas.length} problema(s)`}
            </p>
            {problemas.length > 0 && (
              /* SE DICEN TODOS DE UNA VEZ, con su número de fila. Ir
                 corrigiendo de uno en uno con veinte viajes es como se
                 abandona una importación a la mitad. */
              <ul className="text-sm text-red-800 list-disc pl-5 space-y-0.5">
                {problemas.map((p) => <li key={p}>{p}</li>)}
              </ul>
            )}
            {revision.personas.length > 0 && (
              <div className="max-h-48 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-gray-500 text-left">
                    <tr><th>Nombre</th><th>Usuario</th><th>Puesto</th></tr>
                  </thead>
                  <tbody>
                    {revision.personas.map((p) => (
                      <tr key={p.fila} className="border-t border-gray-100">
                        <td className="py-1">{p.nombre_completo}</td>
                        <td className="py-1 font-mono text-xs">
                          {p.nombre_usuario}
                          {p.usuario_sugerido && (
                            <span className="ml-1 text-[0.65rem] text-gray-500">(propuesto)</span>
                          )}
                        </td>
                        <td className="py-1">{etiquetaRol(p.rol)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <Motivo id="motivo-revisar" cuando={!archivo && !trabajando}>Elige primero el archivo con la lista (arriba); después se revisa y, si está bien, se da de alta.</Motivo>
        <div className="flex gap-2">
          <button
            type="button" disabled={!archivo || trabajando}
            aria-describedby={!archivo ? 'motivo-revisar' : undefined}
            onClick={() => mandar('/usuarios/importar/revisar', setRevision)}
            className="flex-1 border border-gray-300 disabled:opacity-60 text-gray-800 font-semibold py-2.5 rounded-lg text-sm"
          >
            {trabajando ? 'Leyendo…' : 'Revisar'}
          </button>
          <button
            type="button"
            disabled={!archivo || trabajando || !revision
                      || problemas.length > 0 || revision.personas.length === 0}
            title={problemas.length > 0
              ? 'Corrige los problemas de arriba: si hay uno solo, no se crea ninguna invitación.'
              : (!revision ? 'Revisa el archivo primero.' : undefined)}
            onClick={() => mandar('/usuarios/importar/aplicar', onCreadas)}
            className="flex-1 bg-[var(--color-marca)] disabled:opacity-60 text-white font-semibold py-2.5 rounded-lg text-sm"
          >
            Dar de alta
          </button>
        </div>
      </div>
    </Modal>
  );
}


// =====================================================================
// LAS CUENTAS DE SISTEMA · R-USR-SIST (decisión del dueño)
// =====================================================================
// Bots e integraciones, en su propia sección y marcadas aparte de las
// personas. No consumen licencia, hay un máximo de 3 por empresa, y no
// entran a la app como personas: su llave es una clave de integración que
// se enseña una sola vez.
function CuentasSistema({ yo }) {
  const [cuentas, setCuentas] = useState(null);   // null = cargando
  const [maximo, setMaximo] = useState(3);
  const [error, setError] = useState(null);
  const [crear, setCrear] = useState(false);
  const [clave, setClave] = useState(null);       // clave recién emitida
  const [retirar, setRetirar] = useState(null);
  const [trabajando, setTrabajando] = useState(null);

  const puede = yo?.rol && JERARQUIA[yo.rol] >= JERARQUIA.ADMIN;

  const cargar = useCallback(() => {
    api.get('/usuarios/cuentas-sistema')
      .then((d) => { setCuentas(d.cuentas); setMaximo(d.maximo); setError(null); })
      .catch((e) => setError(e.message));
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const activas = (cuentas || []).filter((c) => c.activo).length;
  const lleno = activas >= maximo;

  async function regenerar(c) {
    setTrabajando(c.id);
    try {
      const res = await api.post(`/usuarios/cuentas-sistema/${c.id}/clave`, {});
      setClave({ nombre: c.nombre, clave: res.clave_integracion, aviso: res.aviso });
    } catch (e) {
      setError(e.message);
    } finally {
      setTrabajando(null);
    }
  }

  return (
    <section className="space-y-3 border-t border-gray-200 pt-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="max-w-xl">
          <h3 className="text-base font-semibold text-gray-900">Cuentas de sistema</h3>
          <Ayuda id="seguridad.cuentas_sistema.pantalla" />
        </div>
        {puede && (
          <div className="flex flex-col items-end gap-1">
            <button
              onClick={() => setCrear(true)}
              disabled={lleno}
              title={lleno
                ? `Ya tienes ${maximo} cuentas de sistema activas. Retira una para crear otra.`
                : undefined}
              className="inline-flex items-center gap-1.5 border border-gray-300 hover:border-gray-400 disabled:opacity-60 text-gray-800 text-sm font-semibold px-3 py-2 rounded-lg"
            >
              <Plus className="h-4 w-4" /> Nueva cuenta de sistema
            </button>
            <Ayuda id="seguridad.cuentas_sistema.crear" />
          </div>
        )}
      </div>

      {cuentas && (
        <p className="text-xs text-gray-500">
          {activas} de {maximo} en uso.
        </p>
      )}

      {error && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5">
          {error}
        </p>
      )}

      {cuentas && cuentas.length === 0 && (
        <EstadoVacio
          icono={Bot}
          titulo="Todavía no hay cuentas de sistema"
          descripcion="Una cuenta de sistema conecta un bot o una integración sin ocupar un lugar de tus usuarios."
        />
      )}

      {cuentas && cuentas.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 [&>*]:min-w-0">
          {cuentas.map((c) => (
            <div
              key={c.id}
              className={`border rounded-xl p-3 space-y-2 ${c.activo ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50'}`}
            >
              <div className="flex items-center gap-2">
                <Bot className="h-4 w-4 text-gray-500 shrink-0" />
                <span className="font-medium text-gray-900 truncate">{c.nombre}</span>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="inline-flex items-center rounded-full bg-[var(--color-marca-suave)] text-[var(--color-marca-oscura)] text-xs font-semibold px-2 py-0.5">
                  Cuenta de sistema
                </span>
                {!c.activo && (
                  <span className="inline-flex items-center rounded-full bg-gray-200 text-gray-700 text-xs font-semibold px-2 py-0.5">
                    Retirada
                  </span>
                )}
              </div>
              {c.motivo && <p className="text-sm text-gray-600">{c.motivo}</p>}
              {puede && c.activo && (
                <div className="flex flex-wrap gap-2 pt-1">
                  <div>
                    <button
                      onClick={() => regenerar(c)}
                      disabled={trabajando === c.id}
                      className="inline-flex items-center gap-1.5 border border-gray-300 hover:border-gray-400 disabled:opacity-60 text-gray-800 text-xs font-semibold px-2.5 py-1.5 rounded-lg"
                    >
                      <KeyRound className="h-3.5 w-3.5" />
                      {trabajando === c.id ? 'Generando…' : 'Generar clave nueva'}
                    </button>
                    <Ayuda id="seguridad.cuentas_sistema.clave" />
                  </div>
                  <div>
                    <button
                      onClick={() => setRetirar(c)}
                      className="inline-flex items-center gap-1.5 border border-red-200 hover:border-red-300 text-red-700 text-xs font-semibold px-2.5 py-1.5 rounded-lg"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Retirar
                    </button>
                    <Ayuda id="seguridad.cuentas_sistema.retirar" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {crear && (
        <ModalCrearCuentaSistema
          onCerrar={() => setCrear(false)}
          onCreada={(res) => {
            setCrear(false);
            setClave({ nombre: res.nombre, clave: res.clave_integracion, aviso: res.aviso });
            cargar();
          }}
        />
      )}
      {clave && <ModalClaveSistema datos={clave} onCerrar={() => setClave(null)} />}
      {retirar && (
        <ModalConfirmar
          titulo={`Retirar «${retirar.nombre}»`}
          descripcion="Su clave de integración deja de servir de inmediato y el bot o la integración que la use se queda sin acceso."
          aviso="No se borra: su historial se conserva, y queda un lugar libre de los 3."
          pedirMotivo={false}
          etiquetaConfirmar="Retirar"
          peligro
          onConfirmar={async () => {
            await api.post(`/usuarios/cuentas-sistema/${retirar.id}/retirar`, {});
            setRetirar(null);
            cargar();
          }}
          onCerrar={() => setRetirar(null)}
        />
      )}
    </section>
  );
}

function ModalCrearCuentaSistema({ onCerrar, onCreada }) {
  const [nombre, setNombre] = useState('');
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const valido = nombre.trim().length >= 1 && motivo.trim().length >= 1;

  async function crear() {
    setEnviando(true);
    setError(null);
    try {
      const res = await api.post('/usuarios/cuentas-sistema',
        { nombre: nombre.trim(), motivo: motivo.trim() });
      onCreada(res);
    } catch (e) {
      setError(e.message);
      setEnviando(false);
    }
  }

  return (
    <Modal titulo="Nueva cuenta de sistema" onCerrar={onCerrar}>
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          Para un bot o una integración. No ocupa un lugar de tus usuarios y
          entra con su propia clave, no con contraseña.
        </p>
        <label className="block">
          <span className="block text-sm font-semibold text-gray-700 mb-1">Nombre</span>
          <input
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Bot de compras, Integración con el ERP…"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </label>
        <label className="block">
          <span className="block text-sm font-semibold text-gray-700 mb-1">Uso</span>
          <input
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Sincroniza pedidos, manda avisos a Slack…"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </label>
        {error && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5">
            {error}
          </p>
        )}
        <button
          onClick={crear}
          disabled={!valido || enviando}
          title={valido ? undefined : 'Escribe un nombre y para qué sirve.'}
          className="w-full bg-[var(--color-marca)] disabled:opacity-60 text-white font-semibold py-2.5 rounded-lg text-sm"
        >
          {enviando ? 'Creando…' : 'Crear cuenta de sistema'}
        </button>
      </div>
    </Modal>
  );
}

function ModalClaveSistema({ datos, onCerrar }) {
  return (
    <Modal titulo="Clave de integración" onCerrar={onCerrar}>
      <div className="space-y-3 text-center">
        <p className="text-sm font-semibold text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
          {datos.aviso || 'Esta es la única vez que se ve. Cópiala ahora.'}
        </p>
        <p className="text-sm text-gray-600">
          Pega esta clave en «{datos.nombre}». Con ella la integración entra a
          TEGO por su cuenta, sin usar una sesión de persona.
        </p>
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
          <div className="text-xs text-gray-500 uppercase">Clave</div>
          <div className="text-xs font-mono break-all text-gray-900">{datos.clave}</div>
        </div>
        <BotonCopiar texto={datos.clave} etiqueta="Copiar clave" />
      </div>
    </Modal>
  );
}
